/**
 * Member model represents a practice-owned patient profile and wraps the Patient FHIR resource.
 * It centralizes patient identity, demographics, and practice ownership for member workflows.
 */
import {
  calculateAge,
  formatHumanName,
  type MedplumClient,
  OperationOutcomeError,
  type PatchOperation,
  resolveId,
} from "@medplum/core";
import type {
  Account,
  Address,
  Bundle,
  ContactPoint,
  HumanName,
  Patient,
} from "@medplum/fhirtypes";
import type { UserInfo } from "@tryvital/vital-node/api";
import type { CreateResourceOptions } from "@vibrant/models/fhir/FhirResource";
import type { ResourceData, ResourceRef, WithId } from "@vibrant/models/fhir/fhir-resource-types";
import { findIdentifier } from "@vibrant/models/fhir/fhir-resource-utils";
import { z } from "zod";
import { NotFoundError } from "@/app/api/errors";
import { unique } from "@/lib/arrays";
import {
  CAL_IDENTIFIER_SYSTEM_USERS,
  CAL_IDENTIFIER_SYSTEM_USERS_EMAIL,
  CAL_IDENTIFIER_SYSTEM_USERS_USERNAME,
} from "@/lib/cal/client";
import { isMedplumResourceMissingError } from "@/lib/errors/is-medplum-resource-missing-error";
import { isMedplumVersionConflictError } from "@/lib/errors/is-medplum-version-conflict-error";
import { paginatedSearch } from "@/lib/fhir/bundle";
import Extension from "@/lib/fhir/Extension";
import {
  buildPatientRemovalClearExtensionsPatch,
  buildPatientRemovalExtensionsPatch,
  getPatientRemoval,
  isPatientRemoved,
  type PatientRemoval,
  type PatientRemovalState,
} from "@/lib/fhir/patient-removal";
import { type FullscriptClient, FullscriptPatientExistsError } from "@/lib/fullscript/client";
import { classifyFullscriptForbidden } from "@/lib/fullscript/forbidden";
import { findReadableFullscriptPatientId } from "@/lib/fullscript/readable-patient-id";
import { createLogger } from "@/lib/logger";
import { getPracticeOrganizationFromMeta } from "@/lib/medplum/compartments";
import { pruneSystemValuePairs, toFhirExtensions } from "@/lib/medplum/utils";
import { time } from "@/lib/time";
import { stripUSPhonePrefix, toE164PhoneNumber } from "@/lib/utils/phone/phone-utils";
import {
  collapseWhitespace,
  normalizeForSearch,
  normalizeNameParts,
  trimToUndefined,
} from "@/lib/utils/string";
import type { VitalLabsClient } from "@/lib/vital/client";
import type { VitalInsuranceResponse } from "@/lib/vital/types";
import { defineFhirModel } from "@/models/base/define-model";
import {
  type ExternalReference,
  ExternalReferenceAmbiguousError,
  externalReferenceToken,
  readExternalReferences,
} from "@/models/base/external-reference";
import { patchWithVersionRetry } from "@/models/base/patch-with-version-retry";
import { Practice } from "@/models/practice/Practice";
import { Service } from "@/models/practice/services/Service";
import type { CalCredentials } from "../provider/CalCredentials";
import { searchOneCalCredentials } from "../provider/CalCredentials";
import {
  type ExtensionPatchRequest,
  findExtension,
  getExtensionPatchOperations,
  getIdentifierPatchOperations,
  type IdentifierPatchRequest,
} from "../utils";
import { MemberAccount } from "./accounts/MemberAccount";
import {
  type AccountRemovalResult,
  closeAccountsForRemovedMember,
  reopenAccountsClosedByRemoval,
} from "./accounts/removal-data";
import {
  buildHumanName,
  getPreferredHumanName,
  getPrimaryHumanName,
  isSameFirstAndLastName,
  mergeHumanNames,
} from "./human-name";
import { Insurance } from "./Insurance";
import { MedicalCondition } from "./MedicalCondition";
import {
  type ExternalPatientCreate,
  isPartnerCreated,
  normalizeMemberEmail,
  toFhirExternalPatient,
} from "./member-external";
import { MEMBER_IDENTIFIER } from "./member-identifiers";
import { MemberTagAssignment } from "./tags/MemberTagAssignment";

const logger = createLogger("Member");

/** Parses a member JSON-string extension, returning undefined for absent or malformed data. */
function parseMemberJsonExtension<T>(value: string | undefined): T | undefined {
  if (!value) return undefined;
  try {
    return JSON.parse(value) as T;
  } catch {
    return undefined;
  }
}

export type MedplumGender = "female" | "male" | "other" | "unknown";
export const MIN_MEMBER_DATE_OF_BIRTH = "1900-01-01";

const MEMBER_EXTENSION = {
  /**
   * Vital Insurance JSON.
   */
  VitalInsurance: "vital.insurance",

  /**
   * ID of the Binary resource containing the member's profile picture.
   */
  ProfilePictureFileId: "vibrant.members.profile-picture-file-id",

  /**
   * JSON object of the member's preferred pharmacy (name, phone, address). Free-text demographic
   * captured during onboarding — not the Photon prescription-routing pharmacy.
   */
  PreferredPharmacy: "vibrant.members.preferred-pharmacy",

  /**
   * JSON object of the member's external primary care provider (name, practice, phone).
   * Distinct from `Patient.generalPractitioner`, which references an internal provider.
   */
  PrimaryCareProvider: "vibrant.members.primary-care-provider",

  /**
   * Hint provider web link for direct navigation to Hint patient page.
   */
  HintProviderWebLink: "vibrant.members.hintProviderWebLink",

  /**
   * JSON list of pending SignWell patient agreement documents keyed by practice.
   */
  PatientAgreementSignwellDocuments: "vibrant.members.patient-agreement-signwell-documents",

  /**
   * JSON object of open-ended marketing attribution captured during patient signup.
   */
  AttributionData: "vibrant.members.attribution-data",

  /**
   * When true, AVS generation may propose new health goals but must not update or remove any
   * existing goal. Feature-level (applies to all of the member's goals), not per-goal.
   */
  GoalsAiLocked: "vibrant.members.goals-ai-locked",
} as const;

const PatientAgreementSignwellDocumentSchema = z.object({
  practiceId: z.string().min(1),
  documentId: z.string().min(1),
  signingUrl: z.string().optional(),
  sentAt: z.string().optional(),
});

const PatientAgreementSignwellDocumentsSchema = z.array(PatientAgreementSignwellDocumentSchema);
const MemberAttributionDataSchema = z.record(z.string(), z.unknown());

const MemberDateOfBirthSchema = z
  .string()
  .date("Date of birth is invalid")
  .refine((value) => value >= MIN_MEMBER_DATE_OF_BIRTH, {
    message: `Date of birth must be on or after ${MIN_MEMBER_DATE_OF_BIRTH}`,
  });

/**
 * Zod schema for required sex at birth values.
 * Junction requires binary sex assignment for lab test reference ranges and result interpretation.
 */
export const RequiredSexAtBirthSchema = z.enum(["male", "female"]);

/**
 * Required sex at birth values (male or female).
 */
export type RequiredSexAtBirth = z.infer<typeof RequiredSexAtBirthSchema>;

export type SerializedMember = Patient;
export type PatientAgreementSignwellDocument = z.infer<
  typeof PatientAgreementSignwellDocumentSchema
>;
export type MemberAttributionData = z.infer<typeof MemberAttributionDataSchema>;

/**
 * Largest serialized attribution blob that may land on a Patient. The booking path applies the
 * same bound to the Appointment extension, so the two surfaces cannot disagree on what fits.
 */
export const MAX_ATTRIBUTION_DATA_BYTES = 8 * 1024;

/**
 * Why {@link Member.recordAttributionData} did or did not write. The skip reasons are telemetry
 * the capture path logs, not errors: a practice's marketing page is not a code defect.
 */
export type RecordAttributionDataOutcome =
  | "recorded"
  | "skipped_empty"
  | "skipped_first_touch"
  | "skipped_oversized";

export interface RecordAttributionDataResult {
  /** The member as stored - unchanged on every skip outcome. */
  member: Member;
  outcome: RecordAttributionDataOutcome;
}

export type EnsureMember =
  | { status: "existing"; member: Member }
  | { status: "created"; member: Member };

/**
 * Ensures practice-scoped member operations include a valid Organization reference.
 */
function requirePracticeReference({ practiceRef }: { practiceRef: ResourceRef }): string {
  if (!practiceRef?.reference) {
    throw new Error("Practice reference is required for member operations.");
  }
  return practiceRef.reference;
}

/**
 * Normalizes optional identifier query values for member lookups.
 */
function normalizeIdentifierQueryValue(value: string | undefined): string | undefined {
  return trimToUndefined(value);
}

/**
 * Normalizes optional email values before persistence and for identifier queries.
 */
function normalizeOptionalEmailValue(value: string | undefined): string | undefined {
  const normalized = trimToUndefined(value);
  return normalized ? normalizeMemberEmail(normalized) : undefined;
}

/**
 * Newest sends a practice keeps on the member until its agreement is filed. A patient who lost
 * the email gets a re-send, and each send appends; the cap bounds the extension without dropping
 * the links a patient can still sign from.
 */
const MAX_SENT_AGREEMENT_DOCUMENTS_PER_PRACTICE = 5;

/**
 * Keeps the newest sent-document records for `practiceId` and passes other practices' records
 * through unchanged. Order within the practice stays chronological.
 */
function capPatientAgreementSignwellDocuments({
  documents,
  practiceId,
}: {
  documents: PatientAgreementSignwellDocument[];
  practiceId: string;
}): PatientAgreementSignwellDocument[] {
  const otherPractices = documents.filter((document) => document.practiceId !== practiceId);
  const forPractice = documents.filter((document) => document.practiceId === practiceId);
  // stable ascending sort, then keep the tail: two sends in the same millisecond stay in order
  const oldestFirst = [...forPractice].sort((first, second) => {
    const firstSentAt = first.sentAt ? time(first.sentAt).valueOf() : 0;
    const secondSentAt = second.sentAt ? time(second.sentAt).valueOf() : 0;
    return firstSentAt - secondSentAt;
  });
  const kept = oldestFirst.slice(-MAX_SENT_AGREEMENT_DOCUMENTS_PER_PRACTICE);
  return [...otherPractices, ...kept];
}

/**
 * Parses pending SignWell agreement metadata stored on the Patient resource.
 */
function parsePatientAgreementSignwellDocuments({
  value,
}: {
  value: string | undefined;
}): PatientAgreementSignwellDocument[] {
  if (!value) {
    return [];
  }

  try {
    const parsed = JSON.parse(value) as unknown;
    const result = PatientAgreementSignwellDocumentsSchema.safeParse(parsed);
    return result.success ? result.data : [];
  } catch {
    return [];
  }
}

/**
 * Parses attribution data stored as JSON on the Patient resource.
 */
function parseMemberAttributionData({
  value,
}: {
  value: string | undefined;
}): MemberAttributionData | undefined {
  if (!value) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(value) as unknown;
    const result = MemberAttributionDataSchema.safeParse(parsed);
    return result.success ? result.data : undefined;
  } catch {
    return undefined;
  }
}

function isPatientWithId(resource: Patient): resource is WithId<Patient> {
  return resource.resourceType === "Patient" && typeof resource.id === "string";
}

// Treat undefined as active to preserve backward compatibility while excluding soft-deleted members.
function isActivePatient(patient: Patient): boolean {
  return patient.active !== false;
}

/**
 * Returns every Member matching `queryTypes` inside the practice, from the first practice-scoping
 * strategy that matches a live chart. When no strategy matches a live chart, it returns the first
 * strategy that matched anything.
 *
 * Returning the whole match set — rather than picking one — is what lets callers that must not
 * guess between duplicate charts detect the collision. Callers wanting the historical
 * single-match behavior take the first element.
 */
async function searchPracticeMembersWithQuery({
  medplum,
  practiceReference,
  queryTypes,
  onlyActive,
  excludeRemoved,
}: {
  medplum: MedplumClient;
  practiceReference: string;
  queryTypes: Record<string, string>;
  onlyActive: boolean;
  excludeRemoved: boolean;
}): Promise<Member[]> {
  const scopedQueries = [
    {
      ...queryTypes,
      "_has:Account:subject:owner": practiceReference,
    },
    {
      ...queryTypes,
      organization: practiceReference,
    },
  ];

  // A removed chart keeps its closed Account, so it can match the `_has:Account` tier while the
  // live replacement (not yet invited, no Account) only matches the `organization` tier. Stopping
  // at the first non-empty tier would hide the live chart behind the tombstone.
  let removedOnlyMatches: Member[] = [];
  for (const scopedQuery of scopedQueries) {
    const patients = await medplum.searchResources("Patient", scopedQuery);
    const active = onlyActive ? patients.filter(isActivePatient) : patients;
    const candidates = excludeRemoved
      ? active.filter((patient) => !isPatientRemoved(patient))
      : active;
    const matches = candidates.filter(isPatientWithId).map((patient) => new Member(patient));
    if (hasLiveMember(matches)) {
      return matches;
    }
    if (!removedOnlyMatches.length) {
      removedOnlyMatches = matches;
    }
  }

  return removedOnlyMatches;
}

function hasLiveMember(members: Member[]): boolean {
  return members.some((member) => !member.isRemoved);
}

/**
 * Normalizes and validates member date of birth values.
 */
function normalizeMemberDateOfBirth(dateOfBirth: string | undefined): string | undefined {
  if (typeof dateOfBirth !== "string") {
    return undefined;
  }
  const normalizedDateOfBirth = dateOfBirth.trim();
  if (normalizedDateOfBirth.length === 0) {
    return undefined;
  }
  return MemberDateOfBirthSchema.parse(normalizedDateOfBirth);
}

function memberToFhirObject(data: MemberData | UpdateMemberData): ResourceData<Patient> {
  const normalizedDateOfBirth = normalizeMemberDateOfBirth(data.dateOfBirth);
  const nameEntries = [
    buildHumanName({ name: data.name, use: "official" }),
    buildHumanName({ name: data.preferredName, use: "usual" }),
  ].filter(Boolean) as HumanName[];
  const normalizedEmail = "email" in data ? normalizeMemberEmail(data.email) : undefined;

  return {
    identifier: pruneSystemValuePairs([
      { system: MEMBER_IDENTIFIER.Email, value: normalizedEmail },
      { system: MEMBER_IDENTIFIER.MedmoPatientId, value: data.medmoPatientId },
      {
        system: MEMBER_IDENTIFIER.PhotonPatientId,
        value: data.photonPatientId,
      },
      { system: MEMBER_IDENTIFIER.VitalUserId, value: data.vitalUserId },
      { system: MEMBER_IDENTIFIER.CalUserId, value: data.calUserId },
      { system: MEMBER_IDENTIFIER.CalUsername, value: data.calUsername },
      {
        system: MEMBER_IDENTIFIER.CalManagedEmail,
        value: normalizeOptionalEmailValue(data.calManagedEmail),
      },
      {
        system: MEMBER_IDENTIFIER.FullscriptPatientId,
        value: data.fullscriptPatientId,
      },
      { system: MEMBER_IDENTIFIER.HintPatientId, value: data.hintPatientId },
    ]),
    name: nameEntries.length > 0 ? nameEntries : undefined,
    birthDate: normalizedDateOfBirth,
    gender: data.gender,
    generalPractitioner: data.primaryProvider ? [data.primaryProvider] : undefined,
    managingOrganization: data.practiceRef,
    address: data.address
      ? [
          {
            line: [data.address?.address1, data.address?.address2].filter(
              (line): line is string => typeof line === "string" && line.length > 0,
            ),
            city: data.address?.city,
            state: data.address?.stateTwoLetterCode,
            postalCode: data.address?.zipCode,
          },
        ]
      : undefined,
    telecom: pruneSystemValuePairs([
      { system: "email", value: normalizedEmail },
      {
        system: "phone",
        value: stripUSPhonePrefix(
          toE164PhoneNumber(("phone" in data ? data.phone : undefined) ?? "") || "",
        ),
      },
    ]),
    contact: data.emergencyContact
      ? [
          {
            name: {
              given: normalizeNameParts(data.emergencyContact.firstName),
              family: collapseWhitespace(data.emergencyContact.lastName),
            },
            // Free-text relationship goes in CodeableConcept.text, not coding.code (a FHIR `code`
            // rejects whitespace as "Invalid code format"). See updateEmergencyContact above.
            relationship: collapseWhitespace(data.emergencyContact.relationship)
              ? [
                  {
                    text: collapseWhitespace(data.emergencyContact.relationship),
                  },
                ]
              : undefined,
            telecom: pruneSystemValuePairs([
              {
                system: "phone",
                value: stripUSPhonePrefix(toE164PhoneNumber(data.emergencyContact.phone) || ""),
              },
              {
                system: "email",
                value: normalizeOptionalEmailValue(data.emergencyContact.email),
              },
            ]) as ContactPoint[] | undefined,
          },
        ]
      : undefined,
    extension: toFhirExtensions([
      {
        url: MEMBER_EXTENSION.VitalInsurance,
        valueString: data.insuranceOnVital ? JSON.stringify(data.insuranceOnVital) : undefined,
      },
      {
        url: MEMBER_EXTENSION.ProfilePictureFileId,
        valueString: data.profilePictureFileId,
      },
      {
        url: MEMBER_EXTENSION.PreferredPharmacy,
        valueString: data.preferredPharmacy ? JSON.stringify(data.preferredPharmacy) : undefined,
      },
      {
        url: MEMBER_EXTENSION.PrimaryCareProvider,
        valueString: data.primaryCareProvider
          ? JSON.stringify(data.primaryCareProvider)
          : undefined,
      },
      {
        url: MEMBER_EXTENSION.HintProviderWebLink,
        valueString: data.hintProviderWebLink,
      },
    ]),
  };
}

const MemberBase = defineFhirModel<
  Patient,
  MemberData,
  typeof MEMBER_IDENTIFIER,
  typeof MEMBER_EXTENSION
>({
  resourceType: "Patient" as const,
  identifiers: MEMBER_IDENTIFIER,
  extensions: MEMBER_EXTENSION,
  compartments: ["practiceOrganization"] as const,
  toFhirCreate: memberToFhirObject,
});

export interface MemberData {
  name: string;
  preferredName?: string;
  email: string;
  phone?: string;
  dateOfBirth?: string; // format YYYY-MM-DD
  gender?: MedplumGender;
  address?: {
    address1: string;
    address2?: string;
    city: string;
    stateTwoLetterCode: string;
    zipCode: string;
  };
  medmoPatientId?: number;
  photonPatientId?: string;
  vitalUserId?: string;
  calUserId?: number;
  calUsername?: string;
  calManagedEmail?: string;
  insuranceOnVital?: VitalInsuranceResponse;
  fullscriptPatientId?: string;
  hintPatientId?: string;
  hintProviderWebLink?: string | null;
  hintMembershipStatus?: string;
  hintMembershipLastSyncedAt?: string;
  practiceRef: ResourceRef;
  primaryProvider?: ResourceRef;
  emergencyContact?: {
    firstName: string;
    lastName: string;
    relationship: string;
    phone: string;
    email?: string;
  };
  profilePictureFileId?: string;
  preferredPharmacy?: MemberPreferredPharmacy;
  primaryCareProvider?: MemberPrimaryCareProvider;
}

/** The member's preferred pharmacy, a free-text onboarding demographic. */
export interface MemberPreferredPharmacy {
  name: string;
  phone?: string;
  address?: string;
}

/** The member's external primary care provider, a free-text onboarding demographic. */
export interface MemberPrimaryCareProvider {
  name: string;
  practice?: string;
  phone?: string;
}

export type UpdateMemberData = Partial<Omit<MemberData, "email">>;

export interface MemberSearchQuery {
  practiceRef: ResourceRef;
  email?: string;
  photonPatientId?: string;
  calManagedEmail?: string;
  fullscriptPatientId?: string;
  hintPatientId?: string;
}

export interface MemberSearchOptions {
  /**
   * Restricts the search to active patients only.
   */
  onlyActive?: boolean;
  /**
   * Drops removed (tombstoned) patients before a scoping strategy counts as a match, so a removed
   * chart that keeps its practice Account cannot hide a live patient the next strategy would find.
   */
  excludeRemoved?: boolean;
}

interface UpdateMemberEmailOptions {
  /**
   * Preloaded result from `findEmailConflictInPractice`.
   * Pass `null` when the caller already checked and found no conflict.
   */
  preloadedEmailConflict?: Member | null;
}

interface UpdateMemberEmailParams extends UpdateMemberEmailOptions {
  memberId: string;
  email: string;
  practiceRef: ResourceRef;
}

interface MemberInPracticeParams {
  request: Omit<MemberData, "practiceRef">;
  practice: ResourceRef;
  options?: Omit<CreateResourceOptions, "practiceOrganization">;
}

interface UpdateMemberParams {
  memberId: string;
  data: UpdateMemberData;
  documentReferenceId?: string;
  surveyResponseId?: string;
}

interface UpdateMemberWithFullscriptPatientParams {
  memberId: string;
  fullscriptPatientId: string;
  context: string;
}

interface UpdatePatientTagsParams {
  memberId: string;
  practiceId: string;
  tagIds: string[];
  existingTagIds?: string[];
}

export class Member extends MemberBase {
  /**
   * Unsafe escape hatch.
   *
   * @deprecated Prefer `Member.findOrCreateInPractice(...)` (default) or
   * `Member.createInPractice(...)` (rare, always-create) so the member is
   * practice-compartmented consistently.
   */
  declare static create: typeof MemberBase.create;

  /**
   * Validation schema for member properties.
   */
  static readonly Schema = {
    name: z.string().min(2, "The name must be at least 2 characters long"),
    preferredName: z
      .string()
      .min(2, "Preferred name must be at least 2 characters long")
      .optional(),
    phone: z.string().min(2, "Phone must be at least 2 characters long"),
    dateOfBirth: MemberDateOfBirthSchema,
    gender: z.enum(["male", "female", "other", "unknown"], {
      message: "Gender is required",
    }),
    geneticBackground: z.string().optional(),
    emergencyContact: {
      firstName: z.string().min(2, "First name must be at least 2 characters long"),
      lastName: z.string().min(2, "Last name must be at least 2 characters long"),
      relationship: z.string().min(2, "Relationship must be at least 2 characters long"),
      phone: z.string().min(2, "Phone must be at least 2 characters long"),
      email: z.string().email("Invalid email address"),
    },
  } as const;

  //------------------------------------------------------------------------------------------------
  //                                     CLASS METHODS
  //------------------------------------------------------------------------------------------------
  /**
   * Translate the vibrant member data into a Patient FHIR resource.
   *
   * @param data - The member data.
   * @returns The Patient FHIR resource object.
   */
  static toFhirObject(data: MemberData | UpdateMemberData): ResourceData<Patient> {
    return memberToFhirObject(data);
  }

  /**
   * Finds a practice-scoped member by email or creates a new one.
   *
   * @param medplum - The Medplum client.
   * @param request - The request to create the member if missing.
   * @param practice - Practice scope for both lookup and compartment assignment.
   * @returns The member and whether it was created.
   *
   * Not race-tolerant: concurrent calls can still create duplicates.
   */
  static async findOrCreateInPractice(
    medplum: MedplumClient,
    { request, practice, options }: MemberInPracticeParams,
  ): Promise<EnsureMember> {
    const requestInPractice: MemberData = { ...request, practiceRef: practice };

    const existingMember = await Member.searchOneWithQuery(medplum, {
      email: requestInPractice.email,
      practiceRef: practice,
    });

    if (existingMember) {
      return { status: "existing", member: existingMember };
    }

    const member = await Member.createInPractice(medplum, { request, practice, options });
    return { status: "created", member };
  }

  /**
   * Always creates a new practice-scoped member.
   *
   * Use `findOrCreateInPractice` by default - this is for the rare case where a
   * caller explicitly wants a fresh `Patient` even if a matching email exists.
   */
  static async createInPractice(
    medplum: MedplumClient,
    { request, practice, options }: MemberInPracticeParams,
  ): Promise<Member> {
    const requestInPractice: MemberData = { ...request, practiceRef: practice };
    return Member.create(medplum, requestInPractice, {
      ...options,
      practiceOrganization: practice,
    });
  }

  /**
   * Creates a partner-written patient, or returns the one that already carries its external
   * reference. One conditional create keyed on that reference, so concurrent requests for one
   * source identity make one Patient.
   *
   * Passes no `practiceOrganization`: the API serving AccessPolicy stamps the compartment, and a
   * create that also assigns one adds a write the policy denies. Performs no read.
   */
  static async createExternal(
    medplum: MedplumClient,
    request: ExternalPatientCreate,
  ): Promise<{ member: Member; wasCreated: boolean }> {
    const criteria = new URLSearchParams({
      identifier: externalReferenceToken(request.externalRef),
    });
    const result = await Member.executeBatch(medplum, {
      resourceType: "Bundle",
      type: "transaction",
      entry: [
        {
          resource: toFhirExternalPatient(request),
          request: { method: "POST", url: "Patient", ifNoneExist: criteria.toString() },
        },
      ],
    });
    const entry = result.entry?.[0];
    if (entry?.resource?.resourceType === "OperationOutcome") {
      throw new OperationOutcomeError(entry.resource);
    }
    if (entry?.resource?.resourceType !== "Patient") {
      throw new Error("External patient transaction returned no Patient");
    }
    const status = entry.response?.status?.split(" ")[0];
    if (status !== "200" && status !== "201") {
      throw new Error("External patient transaction returned an unexpected status");
    }
    return { member: new Member(entry.resource), wasCreated: status === "201" };
  }

  /**
   * The patient carrying one external reference, among the patients the client can read. Two
   * matches throw `ExternalReferenceAmbiguousError`: a data-integrity fault, never a pick.
   */
  static async findByExternalReference(
    medplum: MedplumClient,
    externalRef: ExternalReference,
  ): Promise<Member | undefined> {
    const patients = await Member.searchResources(medplum, {
      identifier: externalReferenceToken(externalRef),
      _count: 2,
    });
    if (patients.length > 1) {
      throw new ExternalReferenceAmbiguousError(patients.flatMap((patient) => patient.id ?? []));
    }
    return patients[0] ? new Member(patients[0]) : undefined;
  }

  /**
   * Applies a partner update as a JSON patch guarded by `meta.versionId`. A version conflict
   * reloads and calls `buildOperations` again, so each attempt plans against the version it
   * patches. No operations means no write: `wasUpdated` stays false and the version does not move.
   */
  static async patchExternal(
    medplum: MedplumClient,
    {
      member,
      buildOperations,
    }: { member: Member; buildOperations: (current: Patient) => PatchOperation[] },
  ): Promise<{ member: Member; wasUpdated: boolean }> {
    let wasUpdated = false;
    const patched = await patchWithVersionRetry<Patient>({
      label: `Patient/${member.id}`,
      initial: member.serialize(),
      loadCurrent: async () => (await Member.getById(medplum, member.id)).serialize(),
      buildOperations: ({ current }) => buildOperations(current),
      patch: async ({ operations }) => {
        const result = await Member.patchResourceWithJsonPatch(medplum, member.id, operations);
        wasUpdated = true;
        return result;
      },
    });
    return { member: new Member(patched), wasUpdated };
  }

  /**
   * Gets many members by id in one round trip, keyed by id.
   *
   * For rendering a list that references members by id — resolving each with `getById` would issue
   * one request per row. Ids the caller can't read (wrong compartment, deleted) are simply absent
   * from the map rather than throwing, so a single bad reference can't fail the whole list.
   *
   * @param medplum - The Medplum client.
   * @param memberIds - The member ids to resolve; duplicates are collapsed.
   * @returns Members keyed by id.
   */
  static async getManyById(
    medplum: MedplumClient,
    memberIds: string[],
  ): Promise<Map<string, Member>> {
    const uniqueIds = Array.from(new Set(memberIds.filter(Boolean)));
    const membersById = new Map<string, Member>();
    if (uniqueIds.length === 0) {
      return membersById;
    }

    // Chunked because the ids ride in the query string, and a long enough list would exceed URL
    // limits. Mirrors the batching in `SurveyResponse.getManyByDocumentReference`.
    const chunkSize = 100;
    for (let index = 0; index < uniqueIds.length; index += chunkSize) {
      const idChunk = uniqueIds.slice(index, index + chunkSize);
      const resources = await Member.searchResources(
        medplum,
        { _id: idChunk.join(",") },
        { limit: idChunk.length },
      );

      for (const resource of resources) {
        if (resource.id) {
          membersById.set(resource.id, new Member(resource));
        }
      }
    }

    return membersById;
  }

  /**
   * Gets a member by ID.
   *
   * @param medplum - The Medplum client.
   * @param memberId - The ID of the member.
   * @returns The member.
   */
  static async getById<T extends typeof MemberBase = typeof Member>(
    this: T,
    medplum: MedplumClient,
    memberId: string,
  ): Promise<InstanceType<T>> {
    // Read, don't search. Medplum meters FHIR interactions in points against a per-minute
    // budget, and a search by `_id` costs 20 where a read by id costs 3. This is one of the
    // hottest reads in the app — document parsing alone issued ~9,800 of them in three hours.
    //
    // The search was there to avoid the 410 a hard-deleted Patient throws (`reject-dataset`
    // hard-deletes quarantined ones, so it does happen). Catching that says the same thing:
    // `isMedplumResourceMissingError` covers 404 and 410 alike.
    try {
      const patient = await medplum.readResource("Patient", memberId);
      return new (this as unknown as new (resource: Patient) => InstanceType<T>)(patient);
    } catch (error) {
      if (isMedplumResourceMissingError(error)) {
        throw new NotFoundError("Member not found");
      }
      throw error;
    }
  }

  /**
   * Clears a stale `Patient.active: false` left behind when a member is
   * re-engaged (re-synced from Hint, re-added to a practice) after a prior chart
   * deletion marked them inactive. Only writes when `active` is explicitly
   * `false`, so active/absent members are never touched.
   *
   * Why: `Patient.active: false` blocks downstream integrations that refuse
   * inactive patients - notably DoseSpot ("Cannot insert an inactive patient").
   * Activation historically only happened on invite/reactivate, so practices
   * that don't invite their patients never cleared the flag. Callers must
   * confirm the member is genuinely active (e.g. has an active practice Account)
   * before calling this. Returns true when a repair write occurred.
   *
   * Removed (tombstoned) members are never self-heal candidates: this is a
   * background repair path (Hint sync, DoseSpot sync), and resurrecting a
   * partner-retracted identity from a background sync would silently undo a
   * removal partners already observed (ENG-4822). Deliberate re-engagement
   * goes through {@link Member.restore}.
   */
  static async ensureActive(medplum: MedplumClient, member: Member): Promise<boolean> {
    if (member.resource.active !== false) {
      return false;
    }
    if (member.isRemoved) {
      logger.warn("ensureActive skipped: member is removed (tombstoned)", {
        memberId: member.id,
        removalReason: member.removal?.reason ?? null,
      });
      return false;
    }
    await Member.patchResource(medplum, { id: member.id, patch: { active: true } });
    return true;
  }

  /**
   * Clears a stale `Patient.active: true` (or absent) left behind when a
   * member's practice Account goes inactive but nothing repaired the chart
   * flag. Only writes when `active` is not already `false`. Returns true when
   * a repair write occurred.
   *
   * Unlike {@link Member.ensureActive}, no tombstone guard is needed: a
   * removed (tombstoned) member is already `active: false` via
   * {@link Member.remove}, so this is a pure no-op for them.
   */
  static async ensureInactive(medplum: MedplumClient, member: Member): Promise<boolean> {
    if (member.resource.active === false) {
      return false;
    }
    await Member.patchResource(medplum, { id: member.id, patch: { active: false } });
    return true;
  }

  /**
   * Removes (tombstones) a member: `active: false` plus removal extensions
   * carrying the reason and, for merges, the superseding Patient. This is the
   * only sanctioned way to take a Patient out of service - never hard-delete
   * one (§5.9 tombstone precondition, ENG-4822): a deleted Patient vanishes
   * from `_lastUpdated` searches, so a partner syncing the roster would keep a
   * record we removed. The tombstoned record stays searchable; roster-style
   * list reads filter it out, point reads resolve it via {@link Member.removal}.
   *
   * Also closes the member's Accounts in every practice, so no Account-driven
   * surface shows the tombstone as a live patient (ENG-5780). See
   * `closeAccountsForRemovedMember` for which Accounts change. Writes more than
   * once: the Patient first, then each Account.
   *
   * The Account half is best-effort and never throws. The tombstone is already
   * written, and callers such as `scripts/medplum/delete-member-user.ts` must
   * still run their own deletes after this call. A failed Account write is
   * logged at error level with its ids. A re-run converges.
   *
   * Re-removal is idempotent (extensions are replaced, not appended). Reversal
   * is {@link Member.restore} - and only that; background repair paths refuse
   * tombstones.
   */
  static async remove(
    medplum: MedplumClient,
    memberId: string,
    removal: PatientRemoval,
  ): Promise<Member> {
    const updated = await Member.patchResource(medplum, {
      id: memberId,
      patch: {
        active: false,
        extension: buildPatientRemovalExtensionsPatch(removal),
      },
    });
    await runAccountRemovalStep({
      memberId,
      verb: "closed",
      step: () => closeAccountsForRemovedMember(medplum, memberId),
    });
    return new Member(updated);
  }

  /**
   * Restores a removed (tombstoned) member: clears the removal extensions and
   * reactivates. The deliberate reversal of {@link Member.remove} - call it
   * only where a human or an owning workflow explicitly re-engages the member
   * (e.g. a migration re-running over its own rollback tombstone). The
   * restored record transitions removed → active in `_lastUpdated` order, so
   * the roster feed delivers the un-retraction.
   *
   * Also reopens the Accounts that were active when {@link Member.remove}
   * closed them. Accounts that were already inactive stay inactive. Writes more
   * than once: the Patient first, then each reopened Account. The Account half
   * is best-effort and never throws, as in {@link Member.remove}.
   */
  static async restore(medplum: MedplumClient, memberId: string): Promise<Member> {
    const updated = await Member.patchResource(medplum, {
      id: memberId,
      patch: {
        active: true,
        extension: buildPatientRemovalClearExtensionsPatch(),
      },
    });
    await runAccountRemovalStep({
      memberId,
      verb: "reopened",
      step: () => reopenAccountsClosedByRemoval(medplum, memberId),
    });
    return new Member(updated);
  }

  /**
   * Records a sent SignWell patient agreement document for a practice.
   *
   * Earlier documents for the same practice stay on the record. A patient may sign from the email
   * of an earlier send, and the completion job files a document only when the member holds its
   * id. `getPatientAgreementSignwellDocument` still returns the newest one for the signing link.
   */
  static async recordPatientAgreementSignwellDocument({
    medplum,
    member,
    practiceId,
    documentId,
    signingUrl,
  }: {
    medplum: MedplumClient;
    member: Member;
    practiceId: string;
    documentId: string;
    signingUrl?: string;
  }): Promise<Member> {
    // a re-send of the same document id replaces its entry; other documents stay, up to the cap
    const existingDocuments = member.patientAgreementSignwellDocuments.filter(
      (document) => document.documentId !== documentId,
    );
    const nextDocuments = capPatientAgreementSignwellDocuments({
      documents: [
        ...existingDocuments,
        {
          practiceId,
          documentId,
          ...(signingUrl ? { signingUrl } : {}),
          sentAt: time().toISOString(),
        },
      ],
      practiceId,
    });
    const resource = member.serialize();
    const patchOperations = getExtensionPatchOperations(resource, [
      {
        resource,
        url: Member.Extension.PatientAgreementSignwellDocuments,
        value: JSON.stringify(nextDocuments),
      },
    ]);

    const updated = await medplum.patchResource("Patient", member.id, patchOperations);
    return new Member(updated);
  }

  /**
   * Drops every sent-document record for `practiceId` from the member. The completion job calls
   * this once the signed agreement is filed. The records exist to verify that completion and to
   * build the signing link, and the extension is parsed on every Patient load, so they do not
   * outlive the agreement. Returns how many records were removed; zero means no write.
   */
  static async clearPatientAgreementSignwellDocuments({
    medplum,
    memberId,
    practiceId,
  }: {
    medplum: MedplumClient;
    memberId: string;
    practiceId: string;
  }): Promise<{ removed: number }> {
    const member = await Member.getById(medplum, memberId);
    const documents = member.patientAgreementSignwellDocuments;
    const remaining = documents.filter((document) => document.practiceId !== practiceId);
    const removed = documents.length - remaining.length;
    if (!removed) {
      return { removed: 0 };
    }

    const resource = member.serialize();
    const patchOperations = getExtensionPatchOperations(resource, [
      {
        resource,
        url: Member.Extension.PatientAgreementSignwellDocuments,
        value: JSON.stringify(remaining),
      },
    ]);
    await medplum.patchResource("Patient", member.id, patchOperations);
    return { removed };
  }

  /**
   * Records the open-ended attribution payload captured during member signup, **first touch**:
   * the earliest payload we ever saw for this member wins and later bookings never replace it.
   *
   * Patient-level attribution answers "how was this patient acquired", so overwriting it with the
   * most recent campaign is the opposite of what a CAC report needs - and the provisioning path
   * runs on every booking, including both the hold and confirm phases of one paid booking. The
   * guard is the extension's presence rather than its parsed value: a stored blob we can no longer
   * parse is still a first touch, and overwriting it would make malformed data self-healing in the
   * one direction that loses the original.
   *
   * Per-booking attribution lives on the Appointment instead
   * ({@link MedicalAppointment.Extension.AttributionData}).
   */
  static async recordAttributionData({
    medplum,
    member,
    attributionData,
  }: {
    medplum: MedplumClient;
    member: Member;
    attributionData: MemberAttributionData;
  }): Promise<RecordAttributionDataResult> {
    if (Object.keys(attributionData).length === 0) {
      return { member, outcome: "skipped_empty" };
    }

    if (member.getExtension(Member.Extension.AttributionData)) {
      return { member, outcome: "skipped_first_touch" };
    }

    const value = JSON.stringify(attributionData);
    // The blob sits on the Patient, and the partner roster feed reads up to 100 Patients per page,
    // so an unbounded blob scales that read cost directly. A full UTM set is under 500 bytes; this
    // only catches a page posting something unintended.
    if (new TextEncoder().encode(value).length > MAX_ATTRIBUTION_DATA_BYTES) {
      return { member, outcome: "skipped_oversized" };
    }

    const resource = member.serialize();
    const patchOperations = getExtensionPatchOperations(resource, [
      {
        resource,
        url: Member.Extension.AttributionData,
        value,
      },
    ]);

    const updated = await medplum.patchResource("Patient", member.id, patchOperations);
    return { member: new Member(updated), outcome: "recorded" };
  }

  /**
   * Locks or unlocks AVS generation's ability to update/remove this member's existing health
   * goals. New-goal proposals are unaffected either way.
   *
   * Takes a bare `memberId` (matching the sibling `Protocol.setAiLocked`) and uses
   * `Member.patchResource` (read-merge-write against the server's current resource, with
   * `extension` smart-merged by URL) rather than hand-computing a JSON Patch from a caller-held
   * `Member` snapshot: that snapshot is often stale (e.g. a rapid lock-then-unlock click before a
   * prior write's subscription update lands), and a patch computed from a stale extension array
   * can't find the entry it means to replace — it appends a duplicate instead, and the getter's
   * first-match lookup keeps reading the old value forever after.
   */
  static async setGoalsAiLocked(
    medplum: MedplumClient,
    memberId: string,
    locked: boolean,
  ): Promise<Member> {
    const resource = await Member.patchResource(medplum, {
      id: memberId,
      patch: {
        extension: [{ url: Member.Extension.GoalsAiLocked, valueBoolean: locked }],
      },
    });
    return new Member(resource);
  }

  /**
   * Searches for a member by Vital/Junction user ID.
   *
   * @param medplum - The Medplum client.
   * @param vitalUserId - The Vital/Junction user ID to search for.
   * @returns The member if found, undefined otherwise.
   */
  static async searchOneByVitalUserId(
    medplum: MedplumClient,
    vitalUserId: string,
  ): Promise<Member | undefined> {
    const patient = await Member.searchOneResource(medplum, {
      identifier: `${Member.Identifier.VitalUserId}|${vitalUserId}`,
    });
    return patient ? new Member(patient) : undefined;
  }

  /**
   * Searches for a member by telecom.
   *
   * @param medplum - The Medplum client.
   * @param telecom - The telecom to search for.
   * @returns The member.
   */
  static async getByTelecom(
    medplum: MedplumClient,
    telecom: { system: string; value: string },
  ): Promise<Member | undefined> {
    let normalizedValue: string | undefined = telecom.value;

    if (telecom.system === "phone") {
      normalizedValue = stripUSPhonePrefix(toE164PhoneNumber(telecom.value) || "");
    }
    if (!normalizedValue) return undefined;

    const searchValue = telecom.system ? `${telecom.system}|${normalizedValue}` : normalizedValue;
    const patient =
      (await medplum.searchOne("Patient", { telecom: searchValue })) ??
      (normalizedValue !== telecom.value
        ? await medplum.searchOne("Patient", { telecom: telecom.value })
        : undefined);
    return patient ? new Member(patient) : undefined;
  }

  /**
   * Reads a member from a Medplum patient resource.
   *
   * @param patient - The Medplum patient resource.
   * @returns The member.
   */
  static fromPatient(patient: Patient) {
    return new Member(patient);
  }

  /**
   * Creates a member from the current user profile.
   *
   * @param medplum - The Medplum client.
   * @returns The member.
   */
  static async fromProfile(medplum: MedplumClient): Promise<Member> {
    const profile = await medplum.getProfileAsync();

    if (profile?.resourceType !== "Patient") {
      throw new Error("Member.fromProfile: User is not a patient");
    }
    return new Member(profile as Patient);
  }

  /**
   * Searches for a member by query.
   *
   * Prefers a live chart over a removed one. Two charts share an email whenever a duplicate is
   * tombstoned rather than deleted, and the search order over that pair is not defined — so the
   * tombstone can win. It then stands in for a chart that is still in use: the invite refuses on
   * `isRemoved` and the provider cannot reach the live chart at all (ENG-5814).
   *
   * A lone removed match still resolves. The caller sees the tombstone and reports it, which is
   * the behavior that keeps a removal from being undone by an invite.
   *
   * Returns the first match when the query resolves to more than one live Member. Callers that
   * must not bind an arbitrary chart — anything keying off a shared email — should use
   * {@link Member.searchAllWithQuery} and refuse to guess instead.
   *
   * @param medplum - The Medplum client.
   * @param searchQuery - The query to search for.
   * @returns The member.
   */
  static async searchOneWithQuery(
    medplum: MedplumClient,
    searchQuery: MemberSearchQuery,
    options: MemberSearchOptions = {},
  ): Promise<Member | undefined> {
    const members = await Member.searchAllWithQuery(medplum, searchQuery, options);
    return members.find((member) => !member.isRemoved) ?? members[0];
  }

  /**
   * Searches for every member matching an identifier query within a practice.
   *
   * Identifiers are meant to be unique per practice, so a result with 2+ entries is a
   * data-integrity collision (most often two charts sharing an email). Callers that write
   * against the resolved Member must treat that case as unresolvable rather than picking one.
   *
   * @param medplum - The Medplum client.
   * @param searchQuery - The query to search for.
   * @returns Every matching member, from the first practice-scoping strategy that matched.
   */
  static async searchAllWithQuery(
    medplum: MedplumClient,
    searchQuery: MemberSearchQuery,
    options: MemberSearchOptions = {},
  ): Promise<Member[]> {
    const { practiceRef, ...identifierQuery } = searchQuery;

    const cleanedIdentifierQuery: Partial<Omit<MemberSearchQuery, "practiceRef">> = {};
    const email = normalizeOptionalEmailValue(identifierQuery.email);
    const calManagedEmail = normalizeOptionalEmailValue(identifierQuery.calManagedEmail);
    const fullscriptPatientId = normalizeIdentifierQueryValue(identifierQuery.fullscriptPatientId);
    const photonPatientId = normalizeIdentifierQueryValue(identifierQuery.photonPatientId);
    const hintPatientId = normalizeIdentifierQueryValue(identifierQuery.hintPatientId);

    if (email) {
      cleanedIdentifierQuery.email = email;
    }
    if (calManagedEmail) {
      cleanedIdentifierQuery.calManagedEmail = calManagedEmail;
    }
    if (fullscriptPatientId) {
      cleanedIdentifierQuery.fullscriptPatientId = fullscriptPatientId;
    }
    if (photonPatientId) {
      cleanedIdentifierQuery.photonPatientId = photonPatientId;
    }
    if (hintPatientId) {
      cleanedIdentifierQuery.hintPatientId = hintPatientId;
    }

    if (Object.keys(cleanedIdentifierQuery).length === 0) {
      return [];
    }

    const practiceReference = requirePracticeReference({ practiceRef });
    const { onlyActive = false, excludeRemoved = false } = options;
    let selectedQueryTypes: Record<string, string> | undefined;
    let telecomEmailQueryTypes: Record<string, string> | undefined;

    // TODO: Fix this. If more than one identifier is provided, the other ones will be silently
    // ignored.
    if (cleanedIdentifierQuery.email) {
      selectedQueryTypes = {
        identifier: `${Member.Identifier.Email}|${cleanedIdentifierQuery.email}`,
      };
      // Legacy patients can still have email only in telecom, so email lookups fall back to that.
      telecomEmailQueryTypes = {
        email: cleanedIdentifierQuery.email,
      };
    }

    if (cleanedIdentifierQuery.calManagedEmail) {
      selectedQueryTypes = {
        identifier: `${CAL_IDENTIFIER_SYSTEM_USERS_EMAIL}|${cleanedIdentifierQuery.calManagedEmail}`,
      };
      telecomEmailQueryTypes = undefined;
    }

    if (cleanedIdentifierQuery.fullscriptPatientId) {
      selectedQueryTypes = {
        identifier: `${Member.Identifier.FullscriptPatientId}|${cleanedIdentifierQuery.fullscriptPatientId}`,
      };
      telecomEmailQueryTypes = undefined;
    }

    if (cleanedIdentifierQuery.photonPatientId) {
      selectedQueryTypes = {
        identifier: `${Member.Identifier.PhotonPatientId}|${cleanedIdentifierQuery.photonPatientId}`,
      };
      telecomEmailQueryTypes = undefined;
    }

    if (cleanedIdentifierQuery.hintPatientId) {
      selectedQueryTypes = {
        identifier: `${Member.Identifier.HintPatientId}|${cleanedIdentifierQuery.hintPatientId}`,
      };
      telecomEmailQueryTypes = undefined;
    }

    // Same rule as the practice-scoping tiers: a tombstone on the identifier must not hide a
    // live legacy chart that has the email only in telecom.
    const identifierMatches = selectedQueryTypes
      ? await searchPracticeMembersWithQuery({
          medplum,
          practiceReference,
          queryTypes: selectedQueryTypes,
          onlyActive,
          excludeRemoved,
        })
      : [];
    if (!telecomEmailQueryTypes || hasLiveMember(identifierMatches)) {
      return identifierMatches;
    }

    const telecomMatches = await searchPracticeMembersWithQuery({
      medplum,
      practiceReference,
      queryTypes: telecomEmailQueryTypes,
      onlyActive,
      excludeRemoved,
    });
    if (hasLiveMember(telecomMatches) || !identifierMatches.length) {
      return telecomMatches;
    }
    return identifierMatches;
  }

  /**
   * Finds another active member in the same practice that already uses the email.
   */
  static async findEmailConflictInPractice({
    medplum,
    memberId,
    email,
    practiceRef,
  }: {
    medplum: MedplumClient;
    memberId: string;
    email: string;
    practiceRef: ResourceRef;
  }): Promise<Member | undefined> {
    const existingMember = await Member.searchOneWithQuery(
      medplum,
      {
        email,
        practiceRef,
      },
      { onlyActive: true },
    );

    if (!existingMember || existingMember.id === memberId) {
      return undefined;
    }

    return existingMember;
  }

  /**
   * Finds the charts in a practice whose first name, last name, and birth date match, whatever
   * their email. Removed (tombstoned) charts are excluded.
   *
   * Email is the identity key everywhere else, so two charts for one person on two addresses
   * never collide on it. This is the lookup that catches that case. Names compare on every
   * `Patient.name` entry, so a preferred name also matches. See `isSameFirstAndLastName`.
   *
   * Reads both practice-scoping tiers and merges them: a chart with no Account matches only the
   * `organization` tier. Returns `[]` when a name part or a valid birth date is missing, because a
   * name alone is too weak a match.
   */
  static async searchByNameAndBirthDateInPractice(
    medplum: MedplumClient,
    {
      practiceRef,
      firstName,
      lastName,
      dateOfBirth,
    }: { practiceRef: ResourceRef; firstName: string; lastName: string; dateOfBirth?: string },
  ): Promise<Member[]> {
    const birthDate = MemberDateOfBirthSchema.safeParse(dateOfBirth?.trim());
    if (!birthDate.success || !normalizeForSearch(firstName) || !normalizeForSearch(lastName)) {
      return [];
    }

    const practiceReference = requirePracticeReference({ practiceRef });
    // server filters on birth date only - its name match folds case but not accents, so a
    // family filter would drop "González" for "Gonzalez". One birth date in one practice is a few
    // charts. The name compare happens below.
    const birthDateQuery = { birthdate: birthDate.data };
    // every page - a miss here creates the duplicate chart this lookup exists to stop
    const scopedQueries = [
      { ...birthDateQuery, "_has:Account:subject:owner": practiceReference },
      { ...birthDateQuery, organization: practiceReference },
    ];
    const patients: Patient[] = [];
    for (const query of scopedQueries) {
      for await (const patient of paginatedSearch<Patient>({
        medplum,
        resourceType: "Patient",
        query,
      })) {
        patients.push(patient);
      }
    }

    const matches = patients
      .filter(isPatientWithId)
      .filter((patient) => !isPatientRemoved(patient))
      .filter((patient) =>
        patient.name?.some((name) => isSameFirstAndLastName(name, { firstName, lastName })),
      );

    // a chart with an Account usually matches both tiers
    const matchesById = new Map(matches.map((patient) => [patient.id, patient]));
    return [...matchesById.values()].map((patient) => new Member(patient));
  }

  /**
   * Searches for all members. Removed (tombstoned) members are excluded - see
   * {@link Member.remove}.
   *
   * @param medplum - The Medplum client.
   * @returns The members.
   */
  static async searchAll(medplum: MedplumClient): Promise<Member[]> {
    // TODO: Fetch all patients, not only the first 1000.
    const patients = await medplum.searchResources("Patient", { _count: 1000 });
    return patients.filter((patient) => !isPatientRemoved(patient)).map(Member.fromPatient);
  }

  /**
   * Searches for members by name. Removed (tombstoned) members are excluded -
   * see {@link Member.remove}.
   *
   * @param medplum - The Medplum client.
   * @param name - The name to search for.
   * @returns The members.
   */
  static async searchByName(medplum: MedplumClient, name: string): Promise<Member[]> {
    const patients = await medplum.searchResources("Patient", `name:contains=${name}`);
    return patients.filter((patient) => !isPatientRemoved(patient)).map(Member.fromPatient);
  }

  /**
   * Searches for members by practice.
   *
   * @param medplum - The Medplum client.
   * @param practice - The practice to search for.
   * @returns The members.
   */
  static async searchByPractice(
    medplum: MedplumClient,
    query: {
      practiceId?: string;
      practiceRef?: string;
      name?: string;
      /** Sort alphabetically by name via the Patient/name search path even without a name filter — for browse-all-patients UIs. */
      sortByName?: boolean;
      count?: number;
      offset?: number;
    },
  ): Promise<{ members: Member[]; total: number }> {
    if (!query.practiceId && !query.practiceRef) {
      throw new Error("Practice ID or reference is required");
    }
    const practiceRef = query.practiceRef ?? `Organization/${query.practiceId}`;
    const hasName = !!query.name && query.name.trim().length > 0;

    // When a name filter is provided (or alphabetical sorting is explicitly requested), search
    // Patients directly using reverse chaining to the Account resource. This allows server-side
    // filtering/sorting by name and avoids fetching large pages and filtering in memory.
    if (hasName || query.sortByName) {
      const bundle: Bundle = await medplum.search("Patient", {
        ...(hasName ? { "name:contains": query.name } : {}),
        "_has:Account:subject:owner": practiceRef,
        "_has:Account:subject:status": "active",
        _sort: "name",
        ...(query.count !== undefined ? { _count: query.count } : {}),
        ...(query.offset !== undefined ? { _offset: query.offset } : {}),
        _total: "accurate",
      });

      // Roster read: exclude removed (tombstoned) members. `total` stays the
      // server's count - it can overcount by the filtered few, which roster
      // pagination tolerates.
      const total = bundle.total ?? 0;
      const entries = bundle.entry ?? [];
      const members = entries
        .map((e) => e.resource)
        .filter((r): r is Patient => !!r && r.resourceType === "Patient")
        .filter((patient) => !isPatientRemoved(patient))
        .map((patient) => new Member(patient));

      return { members, total };
    }

    // Default path: search Accounts owned by the practice and include the Patient subjects.
    const bundle: Bundle = await medplum.search("Account", {
      status: MemberAccount.Status.Active,
      owner: practiceRef,
      _include: "Account:subject",
      _sort: "-_lastUpdated",
      ...(query.count !== undefined ? { _count: query.count } : {}),
      ...(query.offset !== undefined ? { _offset: query.offset } : {}),
      _total: "accurate",
    });

    const total = bundle.total ?? 0;

    if (!bundle.entry) {
      return { members: [], total };
    }

    // filter out non-patient resources, removed (tombstoned) members, and
    // deduplicate by id
    const patients = bundle.entry
      .map((entry) => entry.resource)
      .filter(
        (resource): resource is Patient => resource?.resourceType === "Patient" && !!resource.id,
      )
      .filter((patient) => !isPatientRemoved(patient))
      .reduce<Record<string, Patient>>((acc, patient) => {
        if (patient.id) {
          acc[patient.id] = patient;
        }
        return acc;
      }, {});

    const members = Object.values(patients).map((patient) => new Member(patient));

    return {
      members,
      total,
    };
  }

  /**
   * Fetches every member of a practice by walking {@link searchByPractice}
   * pages to exhaustion.
   *
   * This owns the tombstone-safe pagination contract: `searchByPractice` drops
   * removed (tombstoned) members client-side while `total` still counts server
   * rows, so a page can come back short or empty while later pages hold live
   * members. Termination is driven by the server total (`offset >= total`),
   * never by the filtered page length. Don't hand-roll pagination over
   * `searchByPractice` - use this.
   *
   * @param medplum - The Medplum client.
   * @param query - Practice scope (and optional name filter), no paging fields.
   * @param options - Paging and instrumentation knobs.
   * @returns All live members of the practice.
   */
  static async searchByPracticeAll(
    medplum: MedplumClient,
    query: { practiceId?: string; practiceRef?: string; name?: string },
    options?: {
      /** Members per page. Defaults to 200. */
      pageSize?: number;
      /** Called after each page with the running fetched count and server total. */
      onProgress?: (fetched: number, total: number) => void;
      /** Wraps each page fetch - pass retryMedplumCall in long-running scripts. */
      retry?: <T>(fn: () => Promise<T>) => Promise<T>;
    },
  ): Promise<Member[]> {
    const pageSize = options?.pageSize ?? 200;
    const members: Member[] = [];
    let offset = 0;
    let total = Number.POSITIVE_INFINITY;

    while (offset < total) {
      const fetchPage = () =>
        Member.searchByPractice(medplum, { ...query, count: pageSize, offset });
      const page = options?.retry ? await options.retry(fetchPage) : await fetchPage();

      total = page.total;
      members.push(...page.members);
      offset += pageSize;

      options?.onProgress?.(members.length, total);
    }

    return members;
  }

  /**
   * One page of the roster CHANGE FEED (API v2 tech spec §5.9, ENG-4823): Patients in
   * compound `(_lastUpdated, _id)` order, optionally range-bounded from a watermark.
   *
   * Two deliberate departures from every other member search:
   *
   * - **Removed (tombstoned) members are included.** The feed is the one read surface
   *   where they belong - rendered as tombstones downstream. Every other list read
   *   filters them (see {@link Member.searchByPractice}).
   * - **No practice scoping in the query.** Callers MUST pass a practice-bound client
   *   (the v2 serving identity, §5.6) whose AccessPolicy owns tenancy at the storage
   *   layer. Never call this with a backend/admin client.
   *
   * Pagination contract (verified in ENG-4807): callers re-query by range from their
   * own watermark and tie-skip client-side. NEVER follow this page's Bundle `next`
   * link - offset-based pagination demonstrably skips records under concurrent writes.
   *
   * @param medplum - A practice-bound Medplum client (v2 serving identity).
   * @param query - Range bound (`updatedOnOrAfter`, inclusive ISO instant) and page size.
   * @returns One page of members, server-ordered by `(_lastUpdated, _id)`.
   */
  static async searchRosterChanges(
    medplum: MedplumClient,
    query: { updatedOnOrAfter?: string; count: number },
  ): Promise<Member[]> {
    const bundle: Bundle = await medplum.search("Patient", {
      _sort: "_lastUpdated,_id",
      _count: query.count,
      ...(query.updatedOnOrAfter ? { _lastUpdated: `ge${query.updatedOnOrAfter}` } : {}),
    });

    return (bundle.entry ?? [])
      .map((entry) => entry.resource)
      .filter(
        (resource): resource is Patient => resource?.resourceType === "Patient" && !!resource.id,
      )
      .map((patient) => new Member(patient));
  }

  /**
   * Fetches ALL the members whose primary provider is the given provider.
   *
   * Pages explicitly. Passing no `_count` lands on Medplum's server default of 20, which silently
   * truncates any provider with a real panel — and both callers (provider offboarding
   * reassignment, Hint practitioner sync) need the whole set or they leave patients behind.
   *
   * Termination checks the RAW page length, before the tombstone filter: a page that came back
   * short only because removed members were dropped would end the loop early and skip live
   * members on later pages. Same hazard `searchByPracticeAll` exists to avoid.
   *
   * @param medplum - The Medplum client.
   * @param providerId - The bare provider ID (not a FHIR reference). Use parseResourceId() to extract from a reference.
   * @param options - `pageSize` defaults to 200.
   * @returns an array of members.
   */
  static async searchByPrimaryProvider(
    medplum: MedplumClient,
    providerId: string,
    options?: { pageSize?: number },
  ): Promise<Member[]> {
    if (providerId.includes("/")) {
      throw new Error(
        `searchByPrimaryProvider expects a bare provider ID, got "${providerId}". Use parseResourceId() to extract the ID from a reference.`,
      );
    }

    const pageSize = options?.pageSize ?? 200;
    const members: Member[] = [];
    let offset = 0;

    while (true) {
      const patients = await Member.searchResources(
        medplum,
        { "general-practitioner": `Practitioner/${providerId}` },
        { limit: pageSize, offset, sort: "_id" },
      );

      // Roster-style read: removed (tombstoned) members are excluded.
      members.push(
        ...patients.filter((patient) => !isPatientRemoved(patient)).map(Member.fromPatient),
      );

      if (patients.length < pageSize) {
        return members;
      }
      offset += patients.length;
    }
  }

  /**
   * Updates the emergency contact for the member.
   *
   * @param medplum - The Medplum client.
   * @param emergencyContact - The emergency contact to update.
   * @returns The updated member.
   */
  static async updateEmergencyContact(
    medplum: MedplumClient,
    memberId: string,
    {
      firstName,
      lastName,
      relationship,
      phone,
      email,
    }: {
      firstName: string;
      lastName: string;
      relationship: string;
      phone: string;
      email?: string;
    },
  ) {
    // Relationship is free-text human input ("Husband", "Mother-in-law"), so store it in
    // CodeableConcept.text - never coding.code. A FHIR `code` rejects leading/trailing/internal
    // whitespace, so a mobile-entered value like "Husband " was rejected as "Invalid code format".
    const normalizedRelationship = collapseWhitespace(relationship);
    const resource = await Member.patchResource(medplum, {
      id: memberId,
      patch: {
        contact: [
          {
            name: {
              given: normalizeNameParts(firstName),
              family: collapseWhitespace(lastName),
            },
            relationship: normalizedRelationship ? [{ text: normalizedRelationship }] : undefined,
            telecom: pruneSystemValuePairs([
              {
                system: "phone",
                value: stripUSPhonePrefix(toE164PhoneNumber(phone) || ""),
              },
              { system: "email", value: normalizeOptionalEmailValue(email) },
            ]),
          },
        ],
      },
    });
    return new Member(resource);
  }

  /**
   * Clears the emergency contact for the member.
   *
   * @param medplum - The Medplum client.
   * @param memberId - The ID of the member.
   * @returns The updated member.
   */
  static async clearEmergencyContact(medplum: MedplumClient, memberId: string) {
    const resource = await Member.patchResource(medplum, {
      id: memberId,
      patch: {
        contact: [],
      },
    });
    return new Member(resource);
  }

  /**
   * Updates the member.
   *
   * @param medplum - The Medplum client.
   * @param memberId - The ID of the member.
   * @param data - The data to update.
   * @returns The updated member.
   */
  static async update(
    medplum: MedplumClient,
    { memberId, data, documentReferenceId, surveyResponseId }: UpdateMemberParams,
  ) {
    const patch = Member.toFhirObject(data);

    if (data.name !== undefined || data.preferredName !== undefined) {
      const patient = await medplum.readResource("Patient", memberId);
      patch.name = mergeHumanNames({
        existingNames: patient.name,
        legalName: data.name,
        preferredName: data.preferredName,
      });
    }

    const resource = await Member.patchResource(medplum, { id: memberId, patch });

    const sources = [
      ...(documentReferenceId ? [{ reference: `DocumentReference/${documentReferenceId}` }] : []),
      ...(surveyResponseId ? [{ reference: `QuestionnaireResponse/${surveyResponseId}` }] : []),
    ];

    if (sources.length > 0) {
      try {
        const practiceOrganization = getPracticeOrganizationFromMeta(resource.meta);

        await MemberBase.ensureProvenanceFromRefs(medplum, {
          resourceId: memberId,
          args: {
            sources,
            patientId: memberId,
          },
          options: practiceOrganization ? { practiceOrganization } : undefined,
        });
      } catch (error) {
        logger.error("Error ensuring Provenance for member", {
          memberId,
          error,
        });
      }
    }

    return new Member(resource);
  }

  /**
   * Updates the member's email address atomically.
   * Updates both the telecom email and the email identifier.
   *
   * @param medplum - The Medplum client.
   * @param memberId - The ID of the member.
   * @param email - The new email address.
   * @returns The updated member.
   * @throws Error if email is already in use by another member or if update fails.
   */
  static async updateEmail(
    medplum: MedplumClient,
    { memberId, email, practiceRef, preloadedEmailConflict }: UpdateMemberEmailParams,
  ): Promise<Member> {
    const normalizedEmail = normalizeMemberEmail(email);
    const conflictingMember =
      preloadedEmailConflict !== undefined
        ? (preloadedEmailConflict ?? undefined)
        : await Member.findEmailConflictInPractice({
            medplum,
            memberId,
            email: normalizedEmail,
            practiceRef,
          });

    if (conflictingMember) {
      throw new Error(`Email ${normalizedEmail} is already in use by another member`);
    }

    // Read current patient to check existing email
    const patient = await medplum.readResource("Patient", memberId);
    const telecomEmailIndex =
      patient.telecom?.findIndex((telecom) => telecom.system === "email") ?? -1;
    const identifierIndex =
      patient.identifier?.findIndex((id) => id.system === Member.Identifier.Email) ?? -1;

    const currentTelecomEmail =
      telecomEmailIndex >= 0 && patient.telecom
        ? patient.telecom[telecomEmailIndex]?.value
        : undefined;
    const currentIdentifierEmail =
      identifierIndex >= 0 && patient.identifier
        ? patient.identifier[identifierIndex]?.value
        : undefined;

    const effectiveEmail = currentIdentifierEmail ?? currentTelecomEmail;

    // Skip if email is already set to the same value
    if (normalizeOptionalEmailValue(effectiveEmail) === normalizedEmail) {
      return new Member(patient);
    }

    const patchOperations: PatchOperation[] = [];

    // Update telecom email
    if (telecomEmailIndex >= 0 && patient.telecom?.[telecomEmailIndex]) {
      const existingTelecom = patient.telecom[telecomEmailIndex];
      // Replace existing email while preserving metadata
      patchOperations.push({
        op: "replace",
        path: `/telecom/${telecomEmailIndex}`,
        value: {
          ...existingTelecom,
          system: "email",
          value: normalizedEmail,
        },
      });
    } else {
      // Add new email if none exists
      if (!patient.telecom) {
        patchOperations.push({
          op: "add",
          path: "/telecom",
          value: [{ system: "email", value: normalizedEmail }],
        });
      } else {
        patchOperations.push({
          op: "add",
          path: "/telecom/-",
          value: { system: "email", value: normalizedEmail },
        });
      }
    }

    // Update email identifier
    if (identifierIndex >= 0 && patient.identifier?.[identifierIndex]) {
      const existingIdentifier = patient.identifier[identifierIndex];
      // Replace existing identifier while preserving metadata
      patchOperations.push({
        op: "replace",
        path: `/identifier/${identifierIndex}`,
        value: {
          ...existingIdentifier,
          system: Member.Identifier.Email,
          value: normalizedEmail,
        },
      });
    } else {
      // Add new identifier if none exists
      if (!patient.identifier) {
        patchOperations.push({
          op: "add",
          path: "/identifier",
          value: [{ system: Member.Identifier.Email, value: normalizedEmail }],
        });
      } else {
        patchOperations.push({
          op: "add",
          path: "/identifier/-",
          value: { system: Member.Identifier.Email, value: normalizedEmail },
        });
      }
    }

    // Apply all patch operations
    const updatedPatient = await medplum.patchResource("Patient", memberId, patchOperations);
    return new Member(updatedPatient);
  }

  /**
   * Adds a secondary email to the member's telecom array (does not touch identifiers).
   *
   * This is useful for migrations where we want to preserve an alternate email without
   * changing the primary email.
   *
   * @param medplum - The Medplum client.
   * @param memberId - The member ID.
   * @param email - The secondary email to add.
   * @returns The updated member (no-op if already present).
   */
  static async addSecondaryEmailTelecom(
    medplum: MedplumClient,
    memberId: string,
    email: string,
  ): Promise<Member> {
    const normalizedEmail = normalizeMemberEmail(email);
    const patient = await medplum.readResource("Patient", memberId);

    const alreadyPresent =
      patient.telecom?.some(
        (entry) =>
          entry.system === "email" && normalizeOptionalEmailValue(entry.value) === normalizedEmail,
      ) ?? false;

    if (alreadyPresent) {
      return new Member(patient);
    }

    const patchOperations: PatchOperation[] = [];
    if (!patient.telecom) {
      patchOperations.push({
        op: "add",
        path: "/telecom",
        value: [{ system: "email", value: normalizedEmail }],
      });
    } else {
      patchOperations.push({
        op: "add",
        path: "/telecom/-",
        value: { system: "email", value: normalizedEmail },
      });
    }

    const updatedPatient = await medplum.patchResource("Patient", memberId, patchOperations);
    return new Member(updatedPatient);
  }

  /**
   * Patches a member with a query.
   *
   * @param medplum - The Medplum client.
   * @param memberId - The ID of the member.
   * @param patchQuery - The query to patch the member.
   * @returns The patched member.
   */
  static async patchWithQuery(
    medplum: MedplumClient,
    memberId: string,
    patchQuery: UpdateMemberData,
  ): Promise<Member> {
    let patient = await medplum.readResource("Patient", memberId);

    const patchOperations: PatchOperation[] = [];
    const normalizedDateOfBirth = normalizeMemberDateOfBirth(patchQuery.dateOfBirth);

    if (patchQuery.gender) {
      patchOperations.push({
        op: patient.gender ? "replace" : "add",
        path: "/gender",
        value: patchQuery.gender,
      });
    }

    if (normalizedDateOfBirth) {
      patchOperations.push({
        op: patient.birthDate ? "replace" : "add",
        path: "/birthDate",
        value: normalizedDateOfBirth,
      });
    }

    if (patchQuery.address) {
      const address: Address = {
        line: [patchQuery.address.address1, patchQuery.address.address2].filter(
          (line): line is string => typeof line === "string" && line.length > 0,
        ),
        city: patchQuery.address.city,
        state: patchQuery.address.stateTwoLetterCode,
        postalCode: patchQuery.address.zipCode,
      };

      if (!patient.address) {
        patchOperations.push({
          op: "add",
          path: "/address",
          value: [address],
        });
      } else if (patient.address.length === 0) {
        patchOperations.push({
          op: "add",
          path: "/address/-",
          value: address,
        });
      } else {
        patchOperations.push({
          op: "replace",
          path: "/address/0",
          value: address,
        });
      }
    }

    if (patchQuery.phone) {
      const e164 = toE164PhoneNumber(patchQuery.phone ?? "");
      const normalizedPhone = e164 ? stripUSPhonePrefix(e164) : undefined;
      if (!normalizedPhone) {
        // Skip applying an invalid phone normalization
      } else if (!patient.telecom) {
        patchOperations.push({
          op: "add",
          path: "/telecom",
          value: [{ system: "phone", value: normalizedPhone }],
        });
      } else {
        const phoneIndex = patient.telecom?.findIndex((telecom) => telecom.system === "phone");

        if (phoneIndex !== -1) {
          patchOperations.push({
            op: "replace",
            path: `/telecom/${phoneIndex}`,
            value: { system: "phone", value: normalizedPhone },
          });
        } else {
          patchOperations.push({
            op: "add",
            path: "/telecom/-",
            value: { system: "phone", value: normalizedPhone },
          });
        }
      }
    }

    const identifierPatchRequests: IdentifierPatchRequest[] = [];

    if (patchQuery.medmoPatientId) {
      identifierPatchRequests.push({
        resource: patient,
        system: Member.Identifier.MedmoPatientId,
        value: patchQuery.medmoPatientId.toString(),
      });
    }

    if (patchQuery.photonPatientId) {
      identifierPatchRequests.push({
        resource: patient,
        system: Member.Identifier.PhotonPatientId,
        value: patchQuery.photonPatientId,
      });
    }

    if (patchQuery.vitalUserId) {
      identifierPatchRequests.push({
        resource: patient,
        system: Member.Identifier.VitalUserId,
        value: patchQuery.vitalUserId,
      });
    }

    if (patchQuery.calUserId) {
      identifierPatchRequests.push({
        resource: patient,
        system: CAL_IDENTIFIER_SYSTEM_USERS,
        value: patchQuery.calUserId.toString(),
      });
    }

    if (patchQuery.calUsername) {
      identifierPatchRequests.push({
        resource: patient,
        system: CAL_IDENTIFIER_SYSTEM_USERS_USERNAME,
        value: patchQuery.calUsername,
      });
    }

    if (patchQuery.calManagedEmail) {
      identifierPatchRequests.push({
        resource: patient,
        system: CAL_IDENTIFIER_SYSTEM_USERS_EMAIL,
        value: patchQuery.calManagedEmail,
      });
    }

    // one Fullscript patient id per account (ENG-5823) - add beside the others, never replace
    const fullscriptPatientIdToAdd = patchQuery.fullscriptPatientId;
    const shouldAddFullscriptPatientId =
      !!fullscriptPatientIdToAdd &&
      !new Member(patient).fullscriptPatientIds.includes(fullscriptPatientIdToAdd);

    if (patchQuery.hintPatientId) {
      identifierPatchRequests.push({
        resource: patient,
        system: Member.Identifier.HintPatientId,
        value: patchQuery.hintPatientId,
      });
    }

    const extensionPatchRequests: ExtensionPatchRequest[] = [];

    if (patchQuery.insuranceOnVital) {
      extensionPatchRequests.push({
        resource: patient,
        url: Member.Extension.VitalInsurance,
        value: JSON.stringify(patchQuery.insuranceOnVital),
      });
    }

    if (patchQuery.hintProviderWebLink !== undefined) {
      extensionPatchRequests.push({
        resource: patient,
        url: Member.Extension.HintProviderWebLink,
        value: patchQuery.hintProviderWebLink || null,
      });
    }

    const identifierOperations = getIdentifierPatchOperations(patient, identifierPatchRequests);
    if (shouldAddFullscriptPatientId) {
      const fullscriptIdentifier = {
        system: Member.Identifier.FullscriptPatientId,
        value: fullscriptPatientIdToAdd,
      };
      // an earlier op may have created the identifier array, so append after it
      const hasIdentifierArray =
        !!patient.identifier || identifierOperations.some((op) => op.path === "/identifier");
      identifierOperations.push(
        hasIdentifierArray
          ? { op: "add", path: "/identifier/-", value: fullscriptIdentifier }
          : { op: "add", path: "/identifier", value: [fullscriptIdentifier] },
      );
    }

    patient = await medplum.patchResource("Patient", memberId, [
      ...patchOperations,
      ...identifierOperations,
      ...getExtensionPatchOperations(patient, extensionPatchRequests),
    ]);
    return new Member(patient);
  }

  /**
   * Updates a member with an existing Fullscript patient ID.
   *
   * @param medplum - The Medplum client.
   * @param memberId - The ID of the member.
   * @param fullscriptPatientId - The Fullscript patient ID.
   * @param context - Context string for logging.
   * @returns The updated member.
   */
  private static async updateMemberWithFullscriptPatient(
    medplum: MedplumClient,
    { memberId, fullscriptPatientId, context }: UpdateMemberWithFullscriptPatientParams,
  ): Promise<Member> {
    logger.info(`Starting updateMemberWithFullscriptPatient ${context}`, {
      memberId,
      fullscriptPatientId,
      context,
    });

    // First, get the current member state for comparison
    let currentMember: Member;
    try {
      currentMember = await Member.getById(medplum, memberId);
      logger.info(`Retrieved current member state before update ${context}`, {
        memberId,
        fullscriptPatientId,
        context,
        currentFullscriptPatientId: currentMember.fullscriptPatientId,
        hasExistingFullscriptId: !!currentMember.fullscriptPatientId,
      });
    } catch (error) {
      logger.error(`Failed to retrieve current member ${context}`, {
        memberId,
        fullscriptPatientId,
        context,
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });
      throw error;
    }

    logger.info(`Found existing Fullscript patient ${context}`, {
      memberId,
      fullscriptPatientId,
      context,
      existingFullscriptPatientIds: currentMember.fullscriptPatientIds,
    });

    // Update the member with the existing Fullscript patient ID
    let updatedMember: Member;
    try {
      logger.info(`Attempting to patch member with fullscriptPatientId ${context}`, {
        memberId,
        fullscriptPatientId,
        context,
        patchData: { fullscriptPatientId },
      });

      updatedMember = await Member.addFullscriptPatientId(medplum, memberId, fullscriptPatientId);

      logger.info(`Successfully patched member with fullscriptPatientId ${context}`, {
        memberId,
        fullscriptPatientId,
        context,
        updatedFullscriptPatientIds: updatedMember.fullscriptPatientIds,
        patchWasSuccessful: updatedMember.fullscriptPatientIds.includes(fullscriptPatientId),
      });
    } catch (error) {
      logger.error(`Failed to patch member with fullscriptPatientId ${context}`, {
        memberId,
        fullscriptPatientId,
        context,
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });
      throw error;
    }

    logger.info(`Completed updateMemberWithFullscriptPatient ${context}`, {
      memberId,
      fullscriptPatientId,
      context,
      finalFullscriptPatientIds: updatedMember.fullscriptPatientIds,
      updateSuccessful: updatedMember.fullscriptPatientIds.includes(fullscriptPatientId),
    });

    return updatedMember;
  }

  /**
   * Ensures the member has a Fullscript patient in the calling provider's Fullscript account.
   *
   * Fullscript keeps one patient record per clinic account, so a member can hold one id per
   * account. Reuse a stored id only when this account can read it. Otherwise find the patient in
   * this account by email, or create one, and add that id beside the others (ENG-5823).
   *
   * @param medplum - The Medplum client.
   * @param fullscript - The Fullscript client, authenticated as the calling provider.
   * @param memberId - The ID of the member.
   * @returns The member and the Fullscript patient id for the calling provider's account.
   */
  static async ensureFullscriptUser(
    medplum: MedplumClient,
    fullscript: FullscriptClient,
    memberId: string,
  ): Promise<{ member: Member; fullscriptPatientId: string }> {
    logger.info("Starting ensureFullscriptUser", { memberId });

    const member = await Member.getById(medplum, memberId);

    logger.info("Retrieved member data", {
      memberId,
      hasEmail: !!member.email,
      existingFullscriptPatientId: member.fullscriptPatientId,
      hasExistingFullscriptId: !!member.fullscriptPatientId,
    });

    // an unverifiable id (Fullscript error) throws here - never hand another account's id onward
    const readableFullscriptPatientId = await findReadableFullscriptPatientId({
      fullscript,
      fullscriptPatientIds: member.fullscriptPatientIds,
    });
    if (readableFullscriptPatientId) {
      logger.info("Member already has a Fullscript patient in this account", {
        memberId,
        fullscriptPatientId: readableFullscriptPatientId,
      });
      return { member, fullscriptPatientId: readableFullscriptPatientId };
    }

    let fullscriptUser: { patient: { id: string } } | undefined;

    // First, check if the patient already exists by email
    if (member.email) {
      logger.info("Attempting pre-check search by email", {
        memberId,
      });

      try {
        const existingPatient = await fullscript.searchPatientByEmail(member.email, member);

        logger.info("Pre-check search result", {
          memberId,
          patientFound: !!existingPatient?.patient?.id,
          fullscriptPatientId: existingPatient?.patient?.id,
        });

        if (existingPatient?.patient?.id) {
          logger.info("Found existing patient via pre-check, updating member", {
            memberId,
            fullscriptPatientId: existingPatient.patient.id,
          });

          return {
            member: await Member.updateMemberWithFullscriptPatient(medplum, {
              memberId,
              fullscriptPatientId: existingPatient.patient.id,
              context: "during pre-check",
            }),
            fullscriptPatientId: existingPatient.patient.id,
          };
        }
      } catch (searchError) {
        // a missing scope fails the create too - only a reconnect fixes it
        if (isScopeMissing(searchError)) throw searchError;
        // Log but don't fail - continue to create patient
        logger.warn("Pre-check search for existing patient failed", {
          memberId,
          error: searchError instanceof Error ? searchError.message : String(searchError),
          stack: searchError instanceof Error ? searchError.stack : undefined,
        });
      }
    } else {
      logger.warn("Member has no email, cannot perform pre-check search", {
        memberId,
      });
    }

    logger.info("Attempting to create new patient in Fullscript", {
      memberId,
      hasEmail: !!member.email,
    });

    try {
      // Try to create the patient
      fullscriptUser = await fullscript.createPatient(member);

      logger.info("Patient creation response received", {
        memberId,
        hasResponse: !!fullscriptUser,
        hasPatient: !!fullscriptUser?.patient,
        hasPatientId: !!fullscriptUser?.patient?.id,
        fullscriptPatientId: fullscriptUser?.patient?.id,
      });

      if (!fullscriptUser?.patient?.id) {
        logger.error("Invalid response from Fullscript createUser", {
          memberId,
          hasResponse: !!fullscriptUser,
          hasPatient: !!fullscriptUser?.patient,
        });
        throw new Error("Invalid response from Fullscript createUser");
      }

      logger.info("Successfully created patient in Fullscript", {
        memberId,
        fullscriptPatientId: fullscriptUser.patient.id,
      });
    } catch (error: unknown) {
      // kept as is, so the caller can show the reconnect prompt instead of a generic error
      if (isScopeMissing(error)) throw error;
      logger.error("Error during patient creation", {
        memberId,
        errorType: error instanceof Error ? error.constructor.name : "Unknown",
        errorMessage: error instanceof Error ? error.message : String(error),
        isPatientExistsError: error instanceof FullscriptPatientExistsError,
      });
      // Handle the case where patient already exists
      if (error instanceof FullscriptPatientExistsError) {
        logger.info("Patient already exists, attempting to search by email", {
          memberId,
        });

        try {
          // Search for the existing patient by email
          const existingPatient = await fullscript.searchPatientByEmail(error.email, member);

          logger.info("Post-error search result", {
            memberId,
            patientFound: !!existingPatient?.patient?.id,
            fullscriptPatientId: existingPatient?.patient?.id,
          });

          if (existingPatient?.patient?.id) {
            logger.info("Found existing patient via post-error search, updating member", {
              memberId,
              fullscriptPatientId: existingPatient.patient.id,
            });

            return {
              member: await Member.updateMemberWithFullscriptPatient(medplum, {
                memberId,
                fullscriptPatientId: existingPatient.patient.id,
                context: "after patient exists error",
              }),
              fullscriptPatientId: existingPatient.patient.id,
            };
          } else {
            logger.error("Could not find existing patient by email after patient exists error", {
              memberId,
            });
            throw new Error("Patient exists in Fullscript but could not be found by email");
          }
        } catch (searchError) {
          if (isScopeMissing(searchError)) throw searchError;
          logger.error("Failed to search for existing patient after patient exists error", {
            memberId,
            error: searchError instanceof Error ? searchError.message : String(searchError),
            stack: searchError instanceof Error ? searchError.stack : undefined,
          });
          throw new Error(`Failed to handle existing patient: ${searchError}`);
        }
      }

      // Re-throw other errors
      logger.error("Re-throwing non-patient-exists error", {
        memberId,
        errorType: error instanceof Error ? error.constructor.name : "Unknown",
        errorMessage: error instanceof Error ? error.message : String(error),
      });
      throw new Error(`Failed to create Fullscript user: ${error}`);
    }

    logger.info("Updating member with new fullscriptPatientId", {
      memberId,
      fullscriptPatientId: fullscriptUser.patient.id,
    });

    const updatedMember = await Member.addFullscriptPatientId(
      medplum,
      memberId,
      fullscriptUser.patient.id,
    );

    logger.info("Successfully updated member with fullscriptPatientId", {
      memberId,
      fullscriptPatientId: fullscriptUser.patient.id,
    });

    return { member: updatedMember, fullscriptPatientId: fullscriptUser.patient.id };
  }

  /**
   * Adds a Fullscript patient id to the member without replacing the ids already stored. The
   * versionId test rejects the write on a concurrent edit. On that conflict, re-read the member
   * and try again - a few times - so a server action caller does not fail on an unrelated edit.
   */
  static async addFullscriptPatientId(
    medplum: MedplumClient,
    memberId: string,
    fullscriptPatientId: string,
  ): Promise<Member> {
    const MAX_ATTEMPTS = 3;
    for (let attempt = 1; ; attempt++) {
      const member = await Member.getById(medplum, memberId);
      if (member.fullscriptPatientIds.includes(fullscriptPatientId)) return member;

      const identifier = {
        system: Member.Identifier.FullscriptPatientId,
        value: fullscriptPatientId,
      };
      const versionId = member.resource.meta?.versionId;
      const operations: PatchOperation[] = [
        ...(versionId ? [{ op: "test" as const, path: "/meta/versionId", value: versionId }] : []),
        member.resource.identifier
          ? { op: "add", path: "/identifier/-", value: identifier }
          : { op: "add", path: "/identifier", value: [identifier] },
      ];
      try {
        return new Member(await Member.patchResourceWithJsonPatch(medplum, memberId, operations));
      } catch (error) {
        if (attempt >= MAX_ATTEMPTS || !isMedplumVersionConflictError(error)) throw error;
      }
    }
  }

  static async ensureVitalUser(
    medplum: MedplumClient,
    vital: VitalLabsClient,
    memberId: string,
  ): Promise<{
    member: Member;
    vitalUserId: string;
    insuranceOnVital?: VitalInsuranceResponse;
  }> {
    let member = await Member.getById(medplum, memberId);
    let vitalUserId = member.vitalUserId;

    if (!vitalUserId) {
      logger.info(
        "ensureVitalUser: Member does not have a vital user id. Creating new Vital user",
        {
          memberId,
        },
      );
      // 1. Try to create a new Vital user
      try {
        vitalUserId = await vital.createUser({ clientUserId: memberId });
        logger.info("ensureVitalUser: Successfully created Vital user", {
          memberId,
          vitalUserId,
        });
      } catch (error) {
        logger.error("ensureVitalUser: Error creating Vital user", {
          request: { clientUserId: memberId },
          error,
        });
        throw error;
      }

      member = await Member.patchWithQuery(medplum, memberId, {
        vitalUserId,
      });
    } else {
      logger.info("ensureVitalUser: Member already has a vital user id. Skipping creation", {
        memberId,
        vitalUserId,
      });
    }

    // Junction is the source of truth for the practice a member's labs bill through, so the
    // practice id is the field that makes a failure here attributable. Derive it once.
    const practiceId = resolveId(member.resource.managingOrganization) ?? null;

    // 2. Update the Vital user demographics
    let vitalDemographics: UserInfo | undefined;
    try {
      vitalDemographics = await vital.updateUserDemographics(member, vitalUserId);
      // Never log the demographics themselves - they are name, DOB, address, email and phone.
      logger.info("ensureVitalUser: Vital demographics resolved", {
        memberId,
        practiceId,
        vitalUserId,
      });
    } catch (error) {
      logger.error("ensureVitalUser: Error updating Vital demographics", {
        memberId,
        practiceId,
        vitalUserId,
        error,
      });
    }

    if (!vitalDemographics) {
      // A member is missing demographics when any one of name, DOB, email, phone, gender, or a
      // complete address is absent. The caller reports this as missing insurance, so say here
      // that insurance was never reached.
      logger.warn("ensureVitalUser: Member has no Vital demographics. Skipping insurance sync", {
        memberId,
        practiceId,
        vitalUserId,
      });
      return { member, vitalUserId };
    }

    // 3. Insurance sync
    const currentInsurance = await Insurance.searchOneWithQuery(medplum, {
      memberId,
    });
    if (!currentInsurance) {
      logger.warn("ensureVitalUser: Member has no insurance on file", {
        memberId,
        practiceId,
        vitalUserId,
      });
      return { member, vitalUserId };
    }

    // Log the shape of the Coverage, never its values - payor name, policy number and group
    // number are PHI. An engineer reads the values from Medplum, keyed on memberId.
    const insuranceShape = {
      hasPayorCode: !!currentInsurance.payorCode,
      hasPolicyNumber: !!currentInsurance.policyNumber?.length,
      hasGroupNumber: !!currentInsurance.groupNumber?.length,
    };
    logger.info("ensureVitalUser: Member has insurance on file", {
      memberId,
      practiceId,
      ...insuranceShape,
    });

    let updatedInsuranceOnVital: VitalInsuranceResponse | undefined;

    // Junction says "this policy has no group number" with `group_id: null`; the chart says it
    // with an absent Coverage field, which reads as `undefined`. Fold both to one absent value.
    // A bare `!==` calls that pair a change, so a policy with no group number never reaches the
    // no-op below. Every order then re-ran `upsertInsurance`, which is a fresh `createInsurance`
    // POST rather than an idempotent update.
    const groupNumberOnVital = member.insuranceOnVital?.group_id ?? undefined;
    const groupNumberOnChart = currentInsurance.groupNumber ?? undefined;

    // TODO: add relationship check
    const needsInsuranceSyncToVital =
      !member.insuranceOnVital ||
      member.insuranceOnVital.company.name !== currentInsurance.payorName ||
      member.insuranceOnVital.member_id !== currentInsurance.policyNumber ||
      groupNumberOnVital !== groupNumberOnChart;

    if (needsInsuranceSyncToVital) {
      try {
        logger.info("ensureVitalUser: Member needs insurance sync to Vital", {
          memberId,
          practiceId,
          vitalUserId,
          hadPreviousInsuranceOnVital: !!member.insuranceOnVital,
          ...insuranceShape,
        });
        updatedInsuranceOnVital = await vital.upsertInsurance(
          vitalUserId,
          currentInsurance,
          vitalDemographics,
        );
        logger.info("ensureVitalUser: Upserted insurance on Vital", {
          memberId,
          practiceId,
          vitalUserId,
          didJunctionRegisterInsurance: !!updatedInsuranceOnVital,
        });
      } catch (error) {
        logger.error("ensureVitalUser: Error upserting insurance on Vital", {
          memberId,
          practiceId,
          vitalUserId,
          ...insuranceShape,
          error,
        });
      }

      if (updatedInsuranceOnVital) {
        member = await Member.patchWithQuery(medplum, memberId, {
          insuranceOnVital: updatedInsuranceOnVital,
        });
        logger.info("ensureVitalUser: Synced Junction insurance to medplum", {
          memberId,
          practiceId,
          vitalUserId,
        });
      } else {
        // `upsertInsurance` reports "could not register this payor" by returning undefined, so
        // this branch covers both that and the throw caught above. The usual cause is a payor
        // name that resolves to no Junction payor, which is what a free-typed payor produces.
        logger.warn("ensureVitalUser: Insurance not registered on Junction", {
          memberId,
          practiceId,
          vitalUserId,
          hadPreviousInsuranceOnVital: !!member.insuranceOnVital,
          ...insuranceShape,
        });
      }
    } else {
      // The healthy no-op: Junction already holds what the chart holds.
      logger.info("ensureVitalUser: No insurance sync needed", {
        memberId,
        practiceId,
        vitalUserId,
      });
    }

    // Only a prior one-result match is safe to repair. Junction's payor_code can contain a guess.
    const resolvedPayorCode =
      updatedInsuranceOnVital?.resolvedPayorCode ??
      (!needsInsuranceSyncToVital ? member.insuranceOnVital?.resolvedPayorCode : undefined);

    // Best-effort: a cache write failure must not fail a successful Junction insurance sync.
    if (resolvedPayorCode && !currentInsurance.payorCode) {
      try {
        await Insurance.storeResolvedPayorCode(medplum, currentInsurance.id, resolvedPayorCode);
        logger.info("ensureVitalUser: Stored resolved Junction payor code", {
          memberId,
          practiceId,
          vitalUserId,
          insuranceId: currentInsurance.id,
        });
      } catch (error) {
        logger.warn("ensureVitalUser: Could not store resolved Junction payor code", {
          memberId,
          practiceId,
          vitalUserId,
          insuranceId: currentInsurance.id,
          error,
        });
      }
    }

    // A sync we decided was required but could not complete leaves Junction holding the member's
    // *previous* payor and policy. Falling back to the stored record here would tell the order
    // form insurance is good on Junction, and an insurance-pay order would be billed to the
    // carrier the chart no longer lists. Report absent instead so callers fail closed.
    const isInsuranceStaleOnVital = needsInsuranceSyncToVital && !updatedInsuranceOnVital;

    // Report the outcome the caller actually gets. A single "success" line here logged the
    // fail-closed case as an info-level success, so the one branch that blocks an
    // insurance-pay order was the hardest branch to find.
    if (isInsuranceStaleOnVital) {
      logger.warn("ensureVitalUser: Junction does not hold the member's current insurance", {
        memberId,
        practiceId,
        vitalUserId,
        ...insuranceShape,
      });
    } else {
      logger.info("ensureVitalUser: Synced Vital user and insurance to medplum", {
        memberId,
        practiceId,
        vitalUserId,
      });
    }

    return {
      member,
      vitalUserId,
      insuranceOnVital: isInsuranceStaleOnVital
        ? undefined
        : (updatedInsuranceOnVital ?? member.insuranceOnVital),
    };
  }

  /**
   * Gets the service choices for a member.
   *
   * @param medplum - The Medplum client.
   * @param practiceId - The ID of the practice.
   * @returns The service IDs that are available as choices.
   */
  async getServiceChoices(medplum: MedplumClient, practiceId: string): Promise<Service[]> {
    const account = await MemberAccount.searchActiveForMemberPractice(medplum, {
      member: { reference: `Patient/${this.id}` },
      practice: { reference: `Organization/${practiceId}` },
    });

    if (!account) {
      return [];
    }
    const serviceIds = await account.getProposedServiceIds(medplum);
    if (serviceIds.length === 0) return [];
    const services = await Service.searchByIds(medplum, serviceIds);
    const practiceRef = `Organization/${practiceId}`;
    return services.filter(
      (service) =>
        service.healthcareService.active !== false &&
        service.healthcareService.providedBy?.reference === practiceRef,
    );
  }

  /**
   * Updates patient tags using MemberTagAssignment as the sole source of truth.
   * No denormalization to Patient.meta.tag is performed.
   *
   * This method uses atomic operations (create/removeTagFromPatient) to avoid race conditions.
   * It fetches fresh current tags (unless provided) right before computing the diff,
   * ensuring concurrent tag changes are not lost.
   *
   * @param medplum - The Medplum client.
   * @param memberId - The ID of the member.
   * @param practiceId - The ID of the practice.
   * @param tagIds - The complete set of tag IDs to assign to this member.
   * @param existingTagIds - Provide current tag IDs if you've just fetched them.
   *                         If not provided, fresh tags will be fetched internally.
   * @returns The updated member.
   */
  static async updatePatientTags(
    medplum: MedplumClient,
    { memberId, practiceId, tagIds, existingTagIds }: UpdatePatientTagsParams,
  ): Promise<Member> {
    const patientRef = { reference: `Patient/${memberId}` };
    const practiceRef = { reference: `Organization/${practiceId}` };

    // Fetch fresh current tags right before computing diff to minimize race condition window
    // If caller provided existingTagIds, use it (they're responsible for freshness)
    // Otherwise fetch fresh data to ensure we compute diff from latest state
    const currentTagIds =
      existingTagIds ?? (await Member.getPatientTagIds(medplum, memberId, practiceId));

    // Compute diff from current state to desired state
    const currentTagSet = new Set(currentTagIds);
    const desiredTagSet = new Set(tagIds);

    const tagsToAdd = [...desiredTagSet].filter((id) => !currentTagSet.has(id));
    const tagsToRemove = [...currentTagSet].filter((id) => !desiredTagSet.has(id));

    // Apply diff using atomic operations (idempotent create and removeTagFromPatient)
    // These operations are safe to run concurrently:
    // - create() is idempotent (returns existing assignment if already present)
    // - removeTagFromPatient() checks existence before removing (returns false if already removed)
    await Promise.all([
      // Add new tags
      ...tagsToAdd.map((tagId) =>
        MemberTagAssignment.create(medplum, {
          patient: patientRef,
          practice: practiceRef,
          tagId,
        }),
      ),
      // Remove old tags
      ...tagsToRemove.map((tagId) =>
        MemberTagAssignment.removeTagFromPatient(medplum, patientRef, tagId),
      ),
    ]);

    // Return fresh member (no cache update - we read directly from MemberTagAssignment)
    const freshPatient = await medplum.readResource("Patient", memberId);
    return new Member(freshPatient);
  }

  /**
   * Gets patient tag IDs assigned to a member for a specific practice.
   * Reads directly from MemberTagAssignment (source of truth).
   *
   * @param medplum - The Medplum client.
   * @param memberId - The ID of the member.
   * @param practiceId - The ID of the practice.
   * @returns Array of tag IDs assigned to this member for the practice.
   */
  static async getPatientTagIds(
    medplum: MedplumClient,
    memberId: string,
    practiceId: string,
  ): Promise<string[]> {
    const patientRef = { reference: `Patient/${memberId}` };
    const allAssignments = await MemberTagAssignment.getByPatient(medplum, patientRef);

    // Filter to only this practice's assignments
    const practiceAssignments = allAssignments.filter((a) => a.practiceId === practiceId);

    return practiceAssignments.map((a) => a.tagId).filter((id): id is string => !!id);
  }

  //------------------------------------------------------------------------------------------------
  //                                       GETTERS
  //------------------------------------------------------------------------------------------------
  get id(): string {
    if (!this.resource.id) {
      throw new Error("Member resource must have an id");
    }
    return this.resource.id;
  }

  /**
   * True when the member is removed (tombstoned) - see {@link Member.remove}.
   * Roster-style list reads exclude removed members; point reads return them
   * and resolve this explicitly.
   */
  get isRemoved(): boolean {
    return isPatientRemoved(this.resource);
  }

  /** Whether the API serving identity created this patient (`createExternal`). */
  get isPartnerCreated(): boolean {
    return isPartnerCreated(this.resource);
  }

  /** Partner source identities (API v2 `external_refs`), in stored order. */
  get externalReferences(): ExternalReference[] {
    return readExternalReferences(this.resource.identifier);
  }

  /** Removal state (reason + optional merge target), or undefined when not removed. */
  get removal(): PatientRemovalState | undefined {
    return getPatientRemoval(this.resource);
  }

  /**
   * ISO instant of the record's last change (`meta.lastUpdated`) - the roster feed's
   * ordering time. Absent only on hand-built fixtures; server reads always stamp it.
   */
  get lastUpdated(): string | undefined {
    return this.resource.meta?.lastUpdated;
  }

  get name(): string | undefined {
    const humanName = getPrimaryHumanName({ names: this.resource.name });
    return humanName ? formatHumanName(humanName) : undefined;
  }

  get firstName(): string | undefined {
    const [firstName] = getPrimaryHumanName({ names: this.resource.name })?.given ?? [];
    return firstName || undefined;
  }

  get middleName(): string | undefined {
    const [_firstName, ...otherNames] =
      getPrimaryHumanName({ names: this.resource.name })?.given ?? [];
    return otherNames.join(" ") || undefined;
  }

  get lastName(): string | undefined {
    return getPrimaryHumanName({ names: this.resource.name })?.family;
  }

  get fullName(): string | undefined {
    return [this.firstName, this.middleName, this.lastName].filter(Boolean).join(" ") || undefined;
  }

  /**
   * Returns the preferred (usual) name for the member, if available.
   */
  get preferredName(): string | undefined {
    const humanName = getPreferredHumanName({ names: this.resource.name });
    return humanName ? formatHumanName(humanName) : undefined;
  }

  get previousOccupations(): string | undefined {
    return findExtension(this.resource, Extension.Patient.PreviousOccupations)?.valueString;
  }

  get currentOccupation(): string | undefined {
    return findExtension(this.resource, Extension.Patient.CurrentOccupation)?.valueString;
  }

  get address(): Address | undefined {
    return this.resource.address?.[0];
  }

  get email(): string | undefined {
    let email: string | undefined;

    const identifier = findIdentifier(this.resource, Member.Identifier.Email);

    if (identifier) {
      email = identifier.value;
    } else {
      email = this.resource.telecom?.find((telecom) => telecom.system === "email")?.value;
    }
    return normalizeOptionalEmailValue(email);
  }

  /**
   * Used when creating a Reference object to the member.
   */
  get display(): string | undefined {
    return this.name;
  }

  get phone(): string | undefined {
    return this.resource.telecom?.find((telecom) => telecom.system === "phone")?.value;
  }

  get formattedPhone(): string | undefined {
    if (!this.phone) {
      return undefined;
    }

    // Remove all non-digit characters and validate format
    const digitsOnly = this.phone.replace(/\D/g, "");

    // Check if we have exactly 10 digits
    if (digitsOnly.length !== 10) {
      return this.phone;
    }

    return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`;
  }

  get fax(): string | undefined {
    return this.resource.telecom?.find((telecom) => telecom.system === "fax")?.value;
  }

  get dateOfBirth(): string | undefined {
    return this.resource.birthDate;
  }

  get age(): number | undefined {
    if (!this.resource.birthDate) return undefined;

    // Calculate the age of the patient.
    const age = calculateAge(this.resource.birthDate);
    return age.years;
  }

  get gender(): MedplumGender | undefined {
    return this.resource.gender;
  }

  get addressLine1(): string | undefined {
    return this.resource.address?.[0]?.line?.[0];
  }

  get addressLine2(): string | undefined {
    return this.resource.address?.[0]?.line?.[1];
  }

  get city(): string | undefined {
    return this.resource.address?.[0]?.city;
  }

  get stateTwoLetter(): string | undefined {
    return this.resource.address?.[0]?.state;
  }

  get zipCode(): string | undefined {
    return this.resource.address?.[0]?.postalCode;
  }

  get medmoPatientId(): number | undefined {
    const identifier = findIdentifier(this.resource, Member.Identifier.MedmoPatientId);
    if (!identifier) {
      return undefined;
    }
    const parsed = parseInt(identifier.value ?? "", 10);
    if (Number.isNaN(parsed)) {
      throw new Error(`Invalid Medmo Patient ID: ${identifier.value}`);
    }
    return parsed;
  }

  get photonPatientId(): string | undefined {
    const identifier = findIdentifier(this.resource, Member.Identifier.PhotonPatientId);
    return identifier?.value;
  }

  get hintPatientId(): string | undefined {
    const identifier = findIdentifier(this.resource, Member.Identifier.HintPatientId);
    return identifier?.value;
  }

  get dosespotId(): string | undefined {
    return this.getIdentifier(Member.Identifier.DosespotId)?.toString();
  }

  // Add this getter to the Member class
  get fullscriptPatientId(): string | undefined {
    const identifier = findIdentifier(this.resource, Member.Identifier.FullscriptPatientId);
    return identifier?.value;
  }

  /** Every stored Fullscript patient id - one per Fullscript clinic account (ENG-5823). */
  get fullscriptPatientIds(): string[] {
    return (this.resource.identifier ?? [])
      .filter((identifier) => identifier.system === Member.Identifier.FullscriptPatientId)
      .map((identifier) => identifier.value)
      .filter((value): value is string => !!value);
  }

  get emergencyContact() {
    const contact = this.resource.contact?.[0];
    // Prefer free-text relationship; fall back to legacy coding.code for contacts saved before
    // relationships were stored as CodeableConcept.text.
    const relationshipConcept = contact?.relationship?.[0];
    const relationship = relationshipConcept?.text || relationshipConcept?.coding?.[0]?.code || "";
    return {
      firstName: contact?.name?.given?.[0] || "",
      lastName: contact?.name?.family || "",
      relationship,
      phone: contact?.telecom?.find((t) => t.system === "phone")?.value || "",
      email: contact?.telecom?.find((t) => t.system === "email")?.value || "",
    };
  }

  get vitalUserId(): string | undefined {
    const identifier = findIdentifier(this.resource, Member.Identifier.VitalUserId);
    return identifier?.value;
  }

  get insuranceOnVital(): VitalInsuranceResponse | undefined {
    const extension = findExtension(this.resource, Member.Extension.VitalInsurance);
    if (!extension || typeof extension.valueString !== "string") {
      return undefined;
    }
    return JSON.parse(extension.valueString) as VitalInsuranceResponse;
  }

  get calUserId(): number | undefined {
    const identifier = findIdentifier(this.resource, CAL_IDENTIFIER_SYSTEM_USERS);
    if (!identifier) {
      return undefined;
    }
    return parseInt(identifier.value ?? "", 10);
  }

  get calUsername(): string | undefined {
    const identifier = findIdentifier(this.resource, CAL_IDENTIFIER_SYSTEM_USERS_USERNAME);
    return identifier?.value;
  }

  get primaryProvider(): ResourceRef | undefined {
    const practitioner = this.resource.generalPractitioner?.[0];
    if (!practitioner) {
      return undefined;
    }
    return practitioner as ResourceRef;
  }

  get profilePictureFileId(): string | undefined {
    return this.getExtension(Member.Extension.ProfilePictureFileId)?.valueString;
  }

  get preferredPharmacy(): MemberPreferredPharmacy | undefined {
    return parseMemberJsonExtension<MemberPreferredPharmacy>(
      this.getExtension(Member.Extension.PreferredPharmacy)?.valueString,
    );
  }

  get primaryCareProvider(): MemberPrimaryCareProvider | undefined {
    return parseMemberJsonExtension<MemberPrimaryCareProvider>(
      this.getExtension(Member.Extension.PrimaryCareProvider)?.valueString,
    );
  }

  get hintProviderWebLink(): string | undefined {
    return this.getExtension(Member.Extension.HintProviderWebLink)?.valueString;
  }

  get patientAgreementSignwellDocuments(): PatientAgreementSignwellDocument[] {
    return parsePatientAgreementSignwellDocuments({
      value: this.getExtension(Member.Extension.PatientAgreementSignwellDocuments)?.valueString,
    });
  }

  get attributionData(): MemberAttributionData | undefined {
    return parseMemberAttributionData({
      value: this.getExtension(Member.Extension.AttributionData)?.valueString,
    });
  }

  /** Whether AVS generation is locked out of updating/removing this member's existing health goals. */
  get goalsAiLocked(): boolean {
    return this.getExtension(Member.Extension.GoalsAiLocked)?.valueBoolean ?? false;
  }

  /**
   * Returns the latest pending SignWell patient agreement document for a practice.
   *
   * `capPatientAgreementSignwellDocuments` already guarantees the stored array stays
   * chronological (oldest to newest), including two sends recorded in the same millisecond.
   * Take the last match instead of re-sorting by `sentAt`: two sends that land in the same
   * millisecond carry an equal `sentAt`, and re-sorting on that tied value picks arbitrarily
   * rather than the one recorded last.
   */
  getPatientAgreementSignwellDocument({
    practiceId,
  }: {
    practiceId: string;
  }): PatientAgreementSignwellDocument | undefined {
    const forPractice = this.patientAgreementSignwellDocuments.filter(
      (document) => document.practiceId === practiceId,
    );
    return forPractice[forPractice.length - 1];
  }

  //------------------------------------------------------------------------------------------------
  //                                     INSTANCE METHODS
  //------------------------------------------------------------------------------------------------

  async getAllPractices(medplum: MedplumClient): Promise<Practice[]> {
    const accounts = await this.getAccounts(medplum);
    logger.debug("getAllPractices: accountIds", {
      memberId: this.id,
      accountIds: accounts.map((account) => account.id),
    });

    // Drop owners that don't resolve before joining: `_id: ""` (no accounts) and
    // `_id: "undefined"` (a malformed Account.owner) both match zero Organizations, so an
    // unfiltered join reports "belongs to no practice" instead of the one bad reference.
    const practiceIds = unique(
      accounts
        .map((account) => resolveId(account.owner))
        .filter((practiceId) => !!practiceId?.length),
    );
    logger.debug("getAllPractices: practiceIds", {
      memberId: this.id,
      practiceIds,
      practiceNames: accounts.map((account) => account.owner?.display).filter(Boolean),
    });

    if (!practiceIds.length) {
      return [];
    }

    const practices = await medplum.searchResources("Organization", {
      _id: practiceIds.join(","),
    });

    // Accounts resolved but their Organizations didn't: the signature of AccessPolicy drift,
    // since a denied FHIR search returns an empty bundle rather than a 403. Distinct from a
    // member who genuinely belongs nowhere, and otherwise invisible.
    if (practices.length < practiceIds.length) {
      logger.warn("getAllPractices: fewer practices readable than accounts reference", {
        memberId: this.id,
        practiceIds,
        readablePracticeIds: practices.map((practice) => practice.id),
      });
    }

    return practices.map((practice) => new Practice(practice));
  }

  /**
   * Check if a member has an active account with a practice
   *
   * @param medplum - The Medplum client
   * @param practice - The practice to check
   * @returns Whether the member has an active account with the practice
   */
  async hasActiveAccountWithPractice(medplum: MedplumClient, practice: Practice): Promise<boolean> {
    const memberAccount = await MemberAccount.searchOne(medplum, {
      member: { reference: `Patient/${this.id}` },
      practice: { reference: `Organization/${practice.id}` },
      status: "active",
    });
    return !!memberAccount;
  }

  /**
   * Get the member's active account with a practice
   *
   * @param medplum - The Medplum client
   * @param practice - The practice to check
   * @returns The active member account if found
   */
  async getActiveAccountWithPractice(
    medplum: MedplumClient,
    practice: Practice,
  ): Promise<MemberAccount | undefined> {
    const memberAccount = await MemberAccount.searchOne(medplum, {
      member: { reference: `Patient/${this.id}` },
      practice: { reference: `Organization/${practice.id}` },
      status: "active",
    });
    return memberAccount;
  }

  /**
   * Checks if member was invited without payment requirement.
   * This occurs when the member account has withoutPayment extension set to true.
   *
   * @param medplum - The Medplum client
   * @param practiceId - The ID of the practice
   * @returns True if member was invited without payment, false otherwise
   */
  async wasInvitedWithoutPayment(medplum: MedplumClient, practiceId: string): Promise<boolean> {
    const memberAccount = await MemberAccount.searchOne(medplum, {
      member: { reference: `Patient/${this.id}` },
      practice: { reference: `Organization/${practiceId}` },
    });

    if (!memberAccount) return false;
    return memberAccount.wasInvitedWithoutPayment;
  }

  /**
   * Get a member by their account ID
   *
   * @param medplum - The Medplum client
   * @param accountId - The account ID
   * @returns The member if found
   */
  static async getByAccountId(
    medplum: MedplumClient,
    accountId: string,
  ): Promise<Member | undefined> {
    const account = await MemberAccount.getById(medplum, accountId);
    if (!account) return undefined;

    const patientId = resolveId(account.memberRef);
    if (!patientId) return undefined;

    try {
      return await Member.getById(medplum, patientId);
    } catch {
      return undefined;
    }
  }

  /**
   * Gets the accounts for the member.
   *
   * @param medplum - The Medplum client.
   * @returns The accounts.
   */
  async getAccounts(medplum: MedplumClient): Promise<Account[]> {
    const accounts = await MemberAccount.search(medplum, {
      member: { reference: `Patient/${this.id}` },
    });
    return accounts.map((memberAccount) => memberAccount.serialize());
  }

  async getMedicalConditions(medplum: MedplumClient): Promise<MedicalCondition[]> {
    return await MedicalCondition.search(medplum, {
      patientRef: this.reference,
    });
  }

  async getCalCredentials(medplum: MedplumClient): Promise<CalCredentials | undefined> {
    return searchOneCalCredentials(medplum, {
      memberId: this.id,
    });
  }

  async clearCalUser(medplum: MedplumClient): Promise<Member> {
    const updated = await Member.patchResource(medplum, {
      id: this.id,
      patch: {
        identifier: [
          {
            system: CAL_IDENTIFIER_SYSTEM_USERS,
            value: null,
          },
          {
            system: CAL_IDENTIFIER_SYSTEM_USERS_USERNAME,
            value: null,
          },
          {
            system: CAL_IDENTIFIER_SYSTEM_USERS_EMAIL,
            value: null,
          },
        ],
      },
    });
    return new Member(updated);
  }

  /**
   * Hard deletes are forbidden for Patients (§5.9 tombstone precondition,
   * ENG-4822): a deleted Patient never surfaces in a `_lastUpdated` search, so
   * partner roster mirrors would silently keep the record. Use
   * {@link Member.remove} instead. Genuinely-invisible cleanup (quarantined
   * migration data, staging wipes) goes through its own tooling, not the model.
   */
  static async delete(): Promise<never> {
    throw new Error(
      "Member.delete is forbidden: Patients are tombstoned, never hard-deleted (ENG-4822). " +
        "Use Member.remove(medplum, memberId, { reason }) instead.",
    );
  }

  /** @see Member.delete - same rule for the instance form. */
  async delete(): Promise<never> {
    return Member.delete();
  }

  /**
   * Gets a member by Fullscript patient ID.
   *
   * @param medplum - The Medplum client.
   * @param fullscriptPatientId - The Fullscript patient ID.
   * @returns The member or undefined if not found.
   */
  static async getByFullscriptPatientId(
    medplum: MedplumClient,
    fullscriptPatientId: string,
    practiceRef: ResourceRef,
  ): Promise<Member | undefined> {
    return Member.searchOneWithQuery(medplum, {
      fullscriptPatientId,
      practiceRef,
    });
  }
}

/**
 * Runs the Account half of a removal or restore and logs the outcome. Never throws: the Patient
 * write is already done, so a failure here must not stop the caller's next steps.
 */
async function runAccountRemovalStep({
  memberId,
  verb,
  step,
}: {
  memberId: string;
  verb: "closed" | "reopened";
  step: () => Promise<AccountRemovalResult>;
}): Promise<void> {
  try {
    const { changed, failed } = await step();
    const meta = {
      memberId,
      [`${verb}AccountIds`]: changed.map((account) => account.id),
      [`${verb}PracticeIds`]: changed.map((account) => account.ownerPracticeId ?? null),
      failedAccountIds: failed.map((failure) => failure.accountId),
      failedPracticeIds: failed.map((failure) => failure.practiceId),
    };
    if (failed.length) {
      // error level: the caller got no throw, so this line is the only signal an operator sees
      logger.error(`Member removal state changed; some accounts not ${verb}`, meta);
      return;
    }
    logger.info(`Member removal state changed; accounts ${verb}`, meta);
  } catch (error) {
    // the account search failed - nothing was written
    logger.error("Member removal state changed; account search failed", { memberId, verb, error });
  }
}

/** A Fullscript 403 that names a missing OAuth scope. Only a reconnect fixes it. */
function isScopeMissing(error: unknown) {
  return classifyFullscriptForbidden(error)?.kind === "scope_missing";
}
